import {type AttributeRef, type StandardMeshGeometryAttributes, Operators as Op, GeomBuilderContext} from "@cm/template-nodes"
import type {
    Vec2Literal,
    ShapeBasis,
    PartAttributes,
    QuarterDiscParts,
    RoundedRectBorderParts,
    DualRadiusBorderParts,
    SquareParts,
} from "#mattress/geometry/shapes"
import {
    scale,
    lerp,
    makeLoop,
    makeShapeBasis,
    transformBasis2D,
    transformBasis3D,
    square,
    roundedHalfEdge,
    puncturedSquare,
    roundedRectBorder,
    dualRadiusBorder,
    flipLoop,
    texUVFromBasis,
    bilinear,
    quarterDiscFlatPatches,
} from "#mattress/geometry/shapes"
import {z} from "zod"
import {Matrix4, Matrix3x2} from "@cm/math"

type TwoLoopRingInterface = {
    // clockwise:
    // left: A+
    // top: D+
    // right: A-
    // bottom: D-
    loops: readonly [AttributeRef, AttributeRef] // A, D
    // clockwise:
    // left start
    // left end, top start
    // ...
    manifoldUVs: readonly [Vec2Literal, Vec2Literal, Vec2Literal, Vec2Literal]
}

type SixLoopRingInterface = {
    // clockwise:
    // left: A+
    // top left before corner: B+
    // top left after corner: C+
    // top: D+
    // top right before corner: E+
    // top right after corner: B-
    // right: A-
    // bottom right before corner: F+
    // bottom right after corner: E-
    // bottom: D-
    // bottom left before corner: C-
    // bottom left after corner: F-
    loops: readonly [AttributeRef, AttributeRef, AttributeRef, AttributeRef, AttributeRef, AttributeRef] // A, B, C, D, E, F
    // clockwise:
    // left start
    // left end / top left before corner start
    // top left before corner end / top left after corner start
    // ...
    manifoldUVs: readonly [
        Vec2Literal,
        Vec2Literal,
        Vec2Literal,
        Vec2Literal,
        Vec2Literal,
        Vec2Literal,
        Vec2Literal,
        Vec2Literal,
        Vec2Literal,
        Vec2Literal,
        Vec2Literal,
        Vec2Literal,
    ]
}

function transformAndOffsetPart(basis: ShapeBasis, invert: boolean, ofs: number): ShapeBasis {
    const posOrigin = [0, ofs, 0]
    const x = [1, 0, 0]
    const y = [0, 0, -1]
    const z = [0, invert ? -1 : 1, 0]
    const uvOrigin = [0, 0]
    const u = [invert ? -1 : 1, 0]
    const v = [0, 1]

    const posMatrix = Matrix4.fromArray([x[0], x[1], x[2], 0, y[0], y[1], y[2], 0, z[0], z[1], z[2], 0, posOrigin[0], posOrigin[1], posOrigin[2], 1])
    const uvMatrix = Matrix3x2.fromArray([u[0], u[1], v[0], v[1], uvOrigin[0], uvOrigin[1]])
    return {
        posMatrix: basis.posMatrix.multiply(posMatrix),
        uvMatrix: basis.uvMatrix.multiply(uvMatrix),
    }
}

type BottomSquareParams = {
    width: number
    length: number
    punctureSizeFactor: number
    widthSegmentCount: number
    lengthSegmentCount: number
    innerLoopSegmentCount: number
}

type RectangleInterface = TwoLoopRingInterface & {
    width: number
    length: number
    offset: number
}

type RoundedRectangleInterface = SixLoopRingInterface & {
    width: number
    length: number
    radius: number
    offset: number
}

type PuncturedRectangleInterface = RectangleInterface & {
    innerLoop: AttributeRef
}

function splitSquareIntoParts(square: PartAttributes, ctx: GeomBuilderContext, width: number, length: number): SquareParts {
    // build reference geometry for splitting
    // part : ID
    // top : 0
    // leftTop : 1
    // leftBottom : 2
    // bottom : 3
    // rightTop : 4
    // rightBottom : 5

    const ref = (() => {
        const a = makeLoop(ctx, 1)
        const b = makeLoop(ctx, 1)
        const ab = Op.disjointProduct(a, {a}, b, {b})

        if (width === length) {
            const s = width
            const top = {
                xy: bilinear(ab.a, ab.b, [0, 0], [s, s], [-s, s], [0, 2 * s]),
                ID: ab.a.constInt(0),
            }
            const leftTop = {
                xy: bilinear(ab.a, ab.b, [0, 0], [-s, s], [-2 * s, 0], [-2 * s, 0]),
                ID: ab.a.constInt(1),
            }
            const leftBottom = {
                xy: bilinear(ab.a, ab.b, [0, 0], [-2 * s, 0], [-s, -s], [-s, -s]),
                ID: ab.a.constInt(2),
            }
            const bottom = {
                xy: bilinear(ab.a, ab.b, [0, 0], [-s, -s], [s, -s], [0, -2 * s]),
                ID: ab.a.constInt(3),
            }
            const rightTop = {
                xy: bilinear(ab.a, ab.b, [0, 0], [2 * s, 0], [s, s], [s, s]),
                ID: ab.a.constInt(4),
            }
            const rightBottom = {
                xy: bilinear(ab.a, ab.b, [0, 0], [s, -s], [2 * s, 0], [2 * s, 0]),
                ID: ab.a.constInt(5),
            }

            return Op.merge(top, leftTop, leftBottom, bottom, rightTop, rightBottom)
        } else {
            const s0 = Math.min(width, length)
            const s1 = Math.max(width, length)
            const h = (s1 - s0) / 2

            if (width < length) {
                const top = {
                    xy: texUVFromBasis(makeShapeBasis(undefined, Matrix3x2.fromArray([s0, s0, -s0, s0, 0, h])), ab.a, ab.b),
                    ID: ab.a.constInt(0),
                }
                const leftTop = {
                    xy: bilinear(ab.a, ab.b, [-s0, 0], [0, 0], [-s0, s0 + h], [0, h]),
                    ID: ab.a.constInt(1),
                }
                const leftBottom = {
                    xy: bilinear(ab.a, ab.b, [-s0, -s0 - h], [0, -h], [-s0, 0], [0, 0]),
                    ID: ab.a.constInt(2),
                }
                const bottom = {
                    xy: Op.flip(texUVFromBasis(makeShapeBasis(undefined, Matrix3x2.fromArray([s0, -s0, -s0, -s0, 0, -h])), ab.a, ab.b)),
                    ID: ab.a.constInt(3),
                }
                const rightTop = {
                    xy: bilinear(ab.a, ab.b, [0, 0], [s0, 0], [0, h], [s0, s0 + h]),
                    ID: ab.a.constInt(4),
                }
                const rightBottom = {
                    xy: bilinear(ab.a, ab.b, [0, -h], [s0, -s0 - h], [0, 0], [s0, 0]),
                    ID: ab.a.constInt(5),
                }

                return Op.merge(top, leftTop, leftBottom, bottom, rightTop, rightBottom)
            } else {
                const top = {
                    xy: bilinear(ab.a, ab.b, [-h, 0], [h, 0], [-s1 - h, s1], [s1 + h, s1]),
                    ID: ab.a.constInt(0),
                }
                const leftTop = {
                    xy: bilinear(ab.a, ab.b, [-h, 0], [-s0 - h, s0], [-2 * s0 - h, 0], [-2 * s0 - h, 0]),
                    ID: ab.a.constInt(1),
                }
                const leftBottom = {
                    xy: bilinear(ab.a, ab.b, [-h, 0], [-2 * s0 - h, 0], [-s0 - h, -s0], [-s0 - h, -s0]),
                    ID: ab.a.constInt(2),
                }
                const bottom = {
                    xy: bilinear(ab.a, ab.b, [-s1 - h, -s1], [s1 + h, -s1], [-h, 0], [h, 0]),
                    ID: ab.a.constInt(3),
                }
                const rightTop = {
                    xy: bilinear(ab.a, ab.b, [h, 0], [2 * s0 + h, 0], [s0 + h, s0], [s0 + h, s0]),
                    ID: ab.a.constInt(4),
                }
                const rightBottom = {
                    xy: bilinear(ab.a, ab.b, [h, 0], [s0 + h, -s0], [2 * s0 + h, 0], [2 * s0 + h, 0]),
                    ID: ab.a.constInt(5),
                }

                return Op.merge(top, leftTop, leftBottom, bottom, rightTop, rightBottom)
            }
        }
    })()

    const [joinedSquare, joinedRef] = Op.join(square.texUV, square, ref.xy, ref)
    let squareExtended: PartAttributes & {ID: AttributeRef} = Op.triangulate(joinedSquare.pos, {...joinedSquare, ID: joinedRef.ID})
    squareExtended = {
        pos: squareExtended.pos.weld(1e-4),
        texUV: squareExtended.texUV.weld(1e-4),
        manUV: squareExtended.manUV.weld(1e-5),
        ID: squareExtended.ID,
    }

    const triCollapsedEdgeCountEqZero = Op.primitiveCollapsedEdgeCount(squareExtended.pos).eq(0)
    squareExtended = Op.filter(triCollapsedEdgeCountEqZero, {...squareExtended, filterPredicate: triCollapsedEdgeCountEqZero})

    const eqTopID = squareExtended.ID.eq(0)
    const [top, topRest] = Op.partition(eqTopID, {...squareExtended, eqTopID})
    const eqLeftTopID = topRest.ID.eq(1)
    const [leftTop, leftTopRest] = Op.partition(eqLeftTopID, {...topRest, eqLeftTopID})
    const eqLeftBottomID = leftTopRest.ID.eq(2)
    const [leftBottom, leftBottomRest] = Op.partition(eqLeftBottomID, {...leftTopRest, eqLeftBottomID})
    const eqBottomID = leftBottomRest.ID.eq(3)
    const [bottom, bottomRest] = Op.partition(eqBottomID, {...leftBottomRest, eqBottomID})
    const eqRightTopID = bottomRest.ID.eq(4)
    const [rightTop, rightBottom] = Op.partition(eqRightTopID, {...bottomRest, eqRightTopID})

    return {
        top,
        leftTop,
        leftBottom,
        bottom,
        rightTop,
        rightBottom,
    }
}

type SplitSides<T extends {left: PartAttributes; right: PartAttributes}> = Omit<T, "left" | "right"> & {
    leftTop: PartAttributes
    leftBottom: PartAttributes
    rightTop: PartAttributes
    rightBottom: PartAttributes
}

function splitPartAtV0(part: PartAttributes, ctx: GeomBuilderContext, size: number): readonly [PartAttributes, PartAttributes] {
    const ref = (() => {
        const a = makeLoop(ctx, 1)
        const b = makeLoop(ctx, 1)
        const ab = Op.disjointProduct(a, {a}, b, {b})

        const upper = {
            xy: bilinear(ab.a, ab.b, [-size, 0], [size, 0], [-size, size], [size, size]),
            ID: ab.a.constInt(0),
        }
        const lower = {
            xy: bilinear(ab.a, ab.b, [-size, -size], [size, -size], [-size, 0], [size, 0]),
            ID: ab.a.constInt(1),
        }

        return Op.merge(upper, lower)
    })()

    const [joined, joinedRef] = Op.join(part.texUV, part, ref.xy, ref)
    let split: PartAttributes & {ID: AttributeRef} = Op.triangulate(joined.pos, {...joined, ID: joinedRef.ID})
    split = {
        pos: split.pos.weld(1e-4),
        texUV: split.texUV.weld(1e-4),
        manUV: split.manUV.weld(1e-5),
        ID: split.ID,
    }

    const collapsedEdgeCountEqZero = Op.primitiveCollapsedEdgeCount(split.pos).eq(0)
    split = Op.filter(collapsedEdgeCountEqZero, {...split, filterPredicate: collapsedEdgeCountEqZero})

    const eqUpperID = split.ID.eq(0)
    return Op.partition(eqUpperID, {...split, eqUpperID})
}

function splitBorderSides<T extends {left: PartAttributes; right: PartAttributes}>(parts: T, ctx: GeomBuilderContext, size: number): SplitSides<T> {
    const {left, right, ...rest} = parts
    const [leftTop, leftBottom] = splitPartAtV0(left, ctx, size)
    const [rightTop, rightBottom] = splitPartAtV0(right, ctx, size)
    return {...rest, leftTop, leftBottom, rightTop, rightBottom} as SplitSides<T>
}

function bottomSquare(ctx: GeomBuilderContext, params: BottomSquareParams): readonly [SquareParts, PuncturedRectangleInterface] {
    const c0 = 1
    const c1 = 0.8
    const topology: PuncturedRectangleInterface = {
        loops: [makeLoop(ctx, params.widthSegmentCount), makeLoop(ctx, params.lengthSegmentCount)],
        innerLoop: makeLoop(ctx, params.innerLoopSegmentCount),
        manifoldUVs: [
            [-c1, -c1],
            [-c1, +c1],
            [+c1, +c1],
            [+c1, -c1],
        ],
        width: params.width,
        length: params.length,
        offset: 0,
    }
    const loopA = topology.loops[0]
    const loopB = topology.loops[1]
    const basis = transformAndOffsetPart(makeShapeBasis(), true, topology.offset)
    let punctured = puncturedSquare(
        transformBasis2D(basis, [-params.width / 2, -params.length / 2], [params.width, 0], [0, params.length]),
        loopA,
        loopB,
        topology.innerLoop,
        params.punctureSizeFactor,
        params.punctureSizeFactor,
        topology.manifoldUVs,
        [
            [-c0, -c0],
            [-c0, +c0],
            [+c0, +c0],
            [+c0, -c0],
        ],
    )
    const punctureW = params.punctureSizeFactor * params.width
    const punctureH = params.punctureSizeFactor * params.length
    let fill = square(transformBasis2D(basis, [-punctureW / 2, -punctureH / 2], [punctureW, 0], [0, punctureH]), loopA, loopB, [
        [-1.2, -1.2],
        [-1.2, -1.1],
        [-1.1, -1.1],
        [-1.1, -1.2],
    ])
    const bottomSquare = Op.merge(punctured, fill)

    return [splitSquareIntoParts(bottomSquare, ctx, params.width, params.length), topology]
}

type BottomCornerRadiusParams = {
    innerCornerRadius: number
    radiusParallelSegmentCount: number
    radiusAngularSegmentCount: number
}

type BottomCornerRadiusInterface = RoundedRectangleInterface & {
    innerLoop: AttributeRef
}

function bottomCornerRadius(
    ctx: GeomBuilderContext,
    params: BottomCornerRadiusParams,
    prev: PuncturedRectangleInterface,
): readonly [SplitSides<RoundedRectBorderParts>, BottomCornerRadiusInterface] {
    const loopCount1 = Math.round(params.radiusAngularSegmentCount * 0.5)
    const loopCount2 = params.radiusAngularSegmentCount - loopCount1
    const outer = prev.manifoldUVs
    const sc = 0.8
    const sc2 = 0.2
    const innerCorners: Vec2Literal[] = [scale(outer[0], sc), scale(outer[1], sc), scale(outer[2], sc), scale(outer[3], sc)]
    const topology: BottomCornerRadiusInterface = {
        loops: [prev.loops[0], makeLoop(ctx, loopCount1), makeLoop(ctx, loopCount2), prev.loops[1], makeLoop(ctx, loopCount2), makeLoop(ctx, loopCount1)],
        innerLoop: makeLoop(ctx, params.radiusParallelSegmentCount),
        manifoldUVs: [
            innerCorners[0],
            lerp(innerCorners[0], innerCorners[1], sc2),
            lerp(innerCorners[1], innerCorners[0], sc2),
            innerCorners[1],
            lerp(innerCorners[1], innerCorners[2], sc2),
            lerp(innerCorners[2], innerCorners[1], sc2),
            innerCorners[2],
            lerp(innerCorners[2], innerCorners[3], sc2),
            lerp(innerCorners[3], innerCorners[2], sc2),
            innerCorners[3],
            lerp(innerCorners[3], innerCorners[0], sc2),
            lerp(innerCorners[0], innerCorners[3], sc2),
        ],
        width: prev.width + params.innerCornerRadius * 2,
        length: prev.length + params.innerCornerRadius * 2,
        radius: params.innerCornerRadius,
        offset: prev.offset,
    }
    const basis = transformAndOffsetPart(makeShapeBasis(), true, topology.offset)
    let geom = roundedRectBorder(
        basis,
        topology.loops[0],
        topology.loops[1],
        topology.loops[2],
        topology.loops[3],
        topology.loops[4],
        topology.loops[5],
        topology.innerLoop,
        prev.width,
        prev.length,
        params.innerCornerRadius,
        {outer: topology.manifoldUVs, inner: outer},
    ) // inverted!
    return [splitBorderSides(geom, ctx, Math.max(topology.width, topology.length)), topology]
}

type EdgeRadiusParams = {
    edgeRadius: number
    radiusAngularSegmentCount: number
}

type DualRadiusBorderInterface = RoundedRectangleInterface & {
    innerLoop: AttributeRef
}

function bottomEdgeRadius(
    ctx: GeomBuilderContext,
    params: EdgeRadiusParams,
    prev: BottomCornerRadiusInterface,
): readonly [SplitSides<DualRadiusBorderParts>, DualRadiusBorderInterface] {
    const outer = prev.manifoldUVs
    const sc = 0.8
    const topology: DualRadiusBorderInterface = {
        loops: prev.loops,
        innerLoop: makeLoop(ctx, params.radiusAngularSegmentCount),
        manifoldUVs: outer.map((p) => scale(p, sc)) as unknown as SixLoopRingInterface["manifoldUVs"],
        width: prev.width + params.edgeRadius * 2,
        length: prev.length + params.edgeRadius * 2,
        radius: prev.radius + params.edgeRadius,
        offset: prev.offset + params.edgeRadius,
    }
    const basis = transformAndOffsetPart(makeShapeBasis(), true, topology.offset)
    let geom = dualRadiusBorder(
        basis,
        topology.loops[0],
        topology.loops[1],
        topology.loops[2],
        topology.loops[3],
        topology.loops[4],
        topology.loops[5],
        topology.innerLoop,
        prev.width - prev.radius * 2,
        prev.length - prev.radius * 2,
        topology.radius, // _new_ outer radius
        params.edgeRadius,
        {outer: topology.manifoldUVs, inner: outer},
    ) // inverted!
    return [splitBorderSides(geom, ctx, Math.max(topology.width, topology.length)), topology]
}

type SideParts = {
    halfCorner1: PartAttributes
    side: PartAttributes
    halfCorner2: PartAttributes
}

function side(
    basis: ShapeBasis,
    hLoop1: AttributeRef,
    hLoop2: AttributeRef,
    hLoop3: AttributeRef,
    vLoop: AttributeRef,
    hSize: number,
    vSize: number,
    cornerRadius: number,
    manCoords: Vec2Literal[],
): SideParts {
    let halfCorner1 = roundedHalfEdge(
        transformBasis3D(
            basis,
            [0, vSize, 0], // posOrigin
            [-cornerRadius, 0, 0], // x
            [0, -vSize, 0], // y
            [0, 0, cornerRadius], // z
            [0, vSize], // uvOrigin
            [-cornerRadius, 0], // u
            [0, -vSize], // v
        ),
        flipLoop(hLoop1),
        vLoop,
        [manCoords[2], manCoords[7], manCoords[0], manCoords[1]],
    )
    let side = square(
        transformBasis3D(
            basis,
            [0, 0, 0], // posOrigin
            [hSize - cornerRadius * 2, 0, 0], // x
            [0, vSize, 0], // y
            [0, 0, 1], // z
            [0, 0], // uvOrigin
            [hSize - cornerRadius * 2, 0], // u
            [0, vSize], // v
        ),
        hLoop2,
        vLoop,
        [manCoords[7], manCoords[2], manCoords[3], manCoords[6]],
    )
    let halfCorner2 = roundedHalfEdge(
        transformBasis3D(
            basis,
            [hSize - cornerRadius * 2, 0, 0], // posOrigin
            [cornerRadius, 0, 0], // x
            [0, vSize, 0], // y
            [0, 0, cornerRadius], // z
            [hSize - cornerRadius * 2, 0], // uvOrigin
            [cornerRadius, 0], // u
            [0, vSize], // v
        ),
        hLoop3,
        vLoop,
        [manCoords[6], manCoords[3], manCoords[4], manCoords[5]],
    )
    // return Op.merge(halfCorner1, side, halfCorner2)
    return {halfCorner1, side, halfCorner2}
}

type SidesParams = {
    topRadius: number
    height: number
    loopSegmentCount: number
}

type SidesInterface = RoundedRectangleInterface & {
    innerLoop: AttributeRef
}

function sides(
    ctx: GeomBuilderContext,
    params: SidesParams,
    prev: DualRadiusBorderInterface,
): readonly [{top: SideParts; bottom: SideParts; left: SideParts; right: SideParts}, SidesInterface] {
    const outer = prev.manifoldUVs
    const sc = 0.8
    const inner = outer.map((p) => scale(p, sc)) as unknown as SixLoopRingInterface["manifoldUVs"]
    const topRadiusCircumfrenceAdjust = params.topRadius * (Math.PI / 2 - 1)
    const {height, topRadius} = params
    const {radius: cornerRadius, length, width} = prev
    const innerLoop = makeLoop(ctx, params.loopSegmentCount)
    const basis = transformAndOffsetPart(makeShapeBasis(), false, prev.offset)
    const uStartLeft = -width / 2 - topRadiusCircumfrenceAdjust
    const vStartTop = length / 2 + topRadiusCircumfrenceAdjust
    const uStartRight = width / 2 + topRadiusCircumfrenceAdjust
    const vStartBottom = -length / 2 - topRadiusCircumfrenceAdjust
    const left = side(
        transformBasis3D(
            basis,
            [-width / 2, -(length / 2 - cornerRadius), height], // posOrigin
            [0, 1, 0], // x
            [0, 0, -1], // y
            [-1, 0, 0], // z
            [uStartLeft, -(length / 2 - cornerRadius)], // uvOrigin
            [0, 1], // u
            [-1, 0], // v
        ),
        prev.loops[5],
        prev.loops[0],
        prev.loops[1],
        innerLoop,
        length,
        height,
        cornerRadius,
        [inner[0], outer[0], outer[1], outer[2], outer[3], inner[3], inner[2], inner[1]],
    )
    const right = side(
        transformBasis3D(
            basis,
            [width / 2, length / 2 - cornerRadius, height], // posOrigin
            [0, -1, 0], // x
            [0, 0, -1], // y
            [1, 0, 0], // z
            [uStartRight, length / 2 - cornerRadius], // uvOrigin
            [0, -1], // u
            [1, 0], // v
        ),
        flipLoop(prev.loops[1]),
        flipLoop(prev.loops[0]),
        prev.loops[5],
        innerLoop,
        length,
        height,
        cornerRadius,
        [inner[6], outer[6], outer[7], outer[8], outer[9], inner[9], inner[8], inner[7]],
    )
    const top = side(
        transformBasis3D(
            basis,
            [-(width / 2 - cornerRadius), length / 2, height], // posOrigin
            [1, 0, 0], // x
            [0, 0, -1], // y
            [0, 1, 0], // z
            [-width / 2 + cornerRadius, vStartTop], // uvOrigin
            [1, 0], // u
            [0, 1], // v
        ),
        prev.loops[2],
        prev.loops[3],
        prev.loops[4],
        innerLoop,
        width,
        height,
        cornerRadius,
        [inner[3], outer[3], outer[4], outer[5], outer[6], inner[6], inner[5], inner[4]],
    )
    const bottom = side(
        transformBasis3D(
            basis,
            [width / 2 - cornerRadius, -length / 2, height], // posOrigin
            [-1, 0, 0], // x
            [0, 0, -1], // y
            [0, -1, 0], // z
            [width / 2 - cornerRadius, vStartBottom], // uvOrigin
            [-1, 0], // u
            [0, -1], // v
        ),
        flipLoop(prev.loops[4]),
        flipLoop(prev.loops[3]),
        flipLoop(prev.loops[2]),
        innerLoop,
        width,
        height,
        cornerRadius,
        [inner[9], outer[9], outer[10], outer[11], outer[0], inner[0], inner[11], inner[10]],
    )

    return [
        {
            left,
            right,
            top,
            bottom,
        },
        {
            loops: prev.loops,
            innerLoop,
            manifoldUVs: inner,
            width,
            length,
            radius: cornerRadius,
            offset: prev.offset + height,
        },
    ]
}

function topEdgeRadius(
    ctx: GeomBuilderContext,
    params: EdgeRadiusParams,
    prev: SidesInterface,
): readonly [SplitSides<DualRadiusBorderParts>, DualRadiusBorderInterface] {
    const outer = prev.manifoldUVs
    const sc = 0.8
    const topology: DualRadiusBorderInterface = {
        loops: prev.loops,
        innerLoop: makeLoop(ctx, params.radiusAngularSegmentCount),
        manifoldUVs: outer.map((p) => scale(p, sc)) as unknown as SixLoopRingInterface["manifoldUVs"],
        width: prev.width - params.edgeRadius * 2,
        length: prev.length - params.edgeRadius * 2,
        radius: prev.radius - params.edgeRadius,
        offset: prev.offset + params.edgeRadius,
    }
    const basis = transformAndOffsetPart(makeShapeBasis(), false, prev.offset)
    let geom = dualRadiusBorder(
        basis,
        topology.loops[0],
        topology.loops[1],
        topology.loops[2],
        topology.loops[3],
        topology.loops[4],
        topology.loops[5],
        topology.innerLoop,
        prev.width - prev.radius * 2,
        prev.length - prev.radius * 2,
        prev.radius, // _prev_ outer radius
        params.edgeRadius,
        {outer, inner: topology.manifoldUVs},
    )

    return [splitBorderSides(geom, ctx, Math.max(topology.width, topology.length)), topology]
}

type TopCornerRadiusParams = {
    radiusParallelSegmentCount: number
}

type TopCornerRadiusInterface = RectangleInterface & {
    innerLoop: AttributeRef
}

function topCornerRadius(
    ctx: GeomBuilderContext,
    params: TopCornerRadiusParams,
    prev: DualRadiusBorderInterface,
): readonly [SplitSides<RoundedRectBorderParts>, TopCornerRadiusInterface] {
    const outer = prev.manifoldUVs
    const sc = 0.8
    const topology: TopCornerRadiusInterface = {
        loops: [prev.loops[0], prev.loops[3]],
        innerLoop: makeLoop(ctx, params.radiusParallelSegmentCount),
        manifoldUVs: [scale(outer[0], sc), scale(outer[3], sc), scale(outer[6], sc), scale(outer[9], sc)],
        width: prev.width - prev.radius * 2,
        length: prev.length - prev.radius * 2,
        offset: prev.offset,
    }
    const basis = transformAndOffsetPart(makeShapeBasis(), false, topology.offset)
    let geom = roundedRectBorder(
        basis,
        prev.loops[0],
        prev.loops[1],
        prev.loops[2],
        prev.loops[3],
        prev.loops[4],
        prev.loops[5],
        topology.innerLoop,
        topology.width,
        topology.length,
        prev.radius,
        {outer, inner: topology.manifoldUVs},
    )

    return [splitBorderSides(geom, ctx, Math.max(topology.width, topology.length)), topology]
}

function topSquare(ctx: GeomBuilderContext, prev: TopCornerRadiusInterface): SquareParts {
    const basis = transformAndOffsetPart(makeShapeBasis(), false, prev.offset)
    const topSquare = square(
        transformBasis2D(basis, [-prev.width / 2, -prev.length / 2], [prev.width, 0], [0, prev.length]),
        prev.loops[0],
        prev.loops[1],
        prev.manifoldUVs,
    )

    return splitSquareIntoParts(topSquare, ctx, prev.width, prev.length)
}

const internalMattressParams = z.object({
    cornerRadiusSegments: z.number(),
    topRadiusSegments: z.number(),
    bottomRadiusSegments: z.number(),
    widthSegments: z.number(),
    heightSegments: z.number(),
    lengthSegments: z.number(),
    topRadius: z.number(),
    cornerRadius: z.number(),
    bottomRadius: z.number(),
    maxTopRadius: z.number(),
    maxCornerRadius: z.number(),
    maxBottomRadius: z.number(),
    width: z.number(),
    length: z.number(),
    height: z.number(),
    resolutionMultiplier: z.number(),
})
type InternalMattressParams = z.infer<typeof internalMattressParams>

type MattressGeometryParts<T extends {}> = {
    bottom: {
        square: {
            top: T
            leftTop: T
            leftBottom: T
            bottom: T
            rightTop: T
            rightBottom: T
        }
        corner: {
            bottom: T
            leftTop: T
            leftBottom: T
            rightTop: T
            rightBottom: T
            top: T
            bottomLeft: QuarterDiscParts<T>
            bottomRight: QuarterDiscParts<T>
            topLeft: QuarterDiscParts<T>
            topRight: QuarterDiscParts<T>
        }
        edge: {
            bottom: T
            leftTop: T
            leftBottom: T
            rightTop: T
            rightBottom: T
            top: T
            bottomLeft: T
            bottomRight: T
            topLeft: T
            topRight: T
        }
    }
    sides: {
        bottom: {
            halfCorner1: T
            side: T
            halfCorner2: T
        }
        left: {
            halfCorner1: T
            side: T
            halfCorner2: T
        }
        right: {
            halfCorner1: T
            side: T
            halfCorner2: T
        }
        top: {
            halfCorner1: T
            side: T
            halfCorner2: T
        }
    }
    top: {
        edge: {
            bottom: T
            leftTop: T
            leftBottom: T
            rightTop: T
            rightBottom: T
            top: T
            bottomLeft: T
            bottomRight: T
            topLeft: T
            topRight: T
        }
        corner: {
            bottom: T
            leftTop: T
            leftBottom: T
            rightTop: T
            rightBottom: T
            top: T
            bottomLeft: QuarterDiscParts<T>
            bottomRight: QuarterDiscParts<T>
            topLeft: QuarterDiscParts<T>
            topRight: QuarterDiscParts<T>
        }
        square: {
            top: T
            leftTop: T
            leftBottom: T
            bottom: T
            rightTop: T
            rightBottom: T
        }
    }
}

//////////////////////////////
// part tree
//
// A single runtime description of the geometry part tree, so that generic walkers can replace
// hand-written per-part boilerplate. The assertion at the end of this block is a compile-time
// proof that GEOMETRY_PART_TREE describes exactly the same shape as MattressGeometryParts.

type PartTreeNode =
    | {kind: "leaf"}
    | {kind: "uniform"; keys: readonly string[]; child: PartTreeNode}
    | {kind: "group"; fields: Readonly<Record<string, PartTreeNode>>}

const leafNode: {kind: "leaf"} = {kind: "leaf"}

const uniformNode = <const K extends string, C extends PartTreeNode>(keys: readonly K[], child: C): {kind: "uniform"; keys: readonly K[]; child: C} => ({
    kind: "uniform",
    keys,
    child,
})

const groupNode = <F extends Readonly<Record<string, PartTreeNode>>>(fields: F): {kind: "group"; fields: F} => ({kind: "group", fields})

const RING = ["bottom", "left", "right", "top"] as const
const SQUARE = ["top", "leftTop", "leftBottom", "bottom", "rightTop", "rightBottom"] as const
const EDGE = ["bottom", "leftTop", "leftBottom", "rightTop", "rightBottom", "top", "bottomLeft", "bottomRight", "topLeft", "topRight"] as const
const SIDE = ["halfCorner1", "side", "halfCorner2"] as const
const QUARTER_DISC = ["curvedPatch1", "curvedPatch2", "flatPatch1", "flatPatch2"] as const

const cornerNode = groupNode({
    bottom: leafNode,
    leftTop: leafNode,
    leftBottom: leafNode,
    rightTop: leafNode,
    rightBottom: leafNode,
    top: leafNode,
    bottomLeft: uniformNode(QUARTER_DISC, leafNode),
    bottomRight: uniformNode(QUARTER_DISC, leafNode),
    topLeft: uniformNode(QUARTER_DISC, leafNode),
    topRight: uniformNode(QUARTER_DISC, leafNode),
})

const GEOMETRY_PART_TREE = groupNode({
    bottom: groupNode({
        square: uniformNode(SQUARE, leafNode),
        corner: cornerNode,
        edge: uniformNode(EDGE, leafNode),
    }),
    sides: uniformNode(RING, uniformNode(SIDE, leafNode)),
    top: groupNode({
        edge: uniformNode(EDGE, leafNode),
        corner: cornerNode,
        square: uniformNode(SQUARE, leafNode),
    }),
})

type InstantiateTree<S, T> = S extends {kind: "leaf"}
    ? T
    : S extends {kind: "uniform"; keys: readonly (infer K extends string)[]; child: infer C}
      ? {[P in K]: InstantiateTree<C, T>}
      : S extends {kind: "group"; fields: infer F}
        ? {[P in keyof F]: InstantiateTree<F[P], T>}
        : never

function walkPartTree(node: PartTreeNode, atLeaf: (path: readonly string[]) => unknown, path: readonly string[] = []): unknown {
    switch (node.kind) {
        case "leaf":
            return atLeaf(path)
        case "uniform":
            return Object.fromEntries(node.keys.map((key) => [key, walkPartTree(node.child, atLeaf, [...path, key])]))
        case "group":
            return Object.fromEntries(Object.entries(node.fields).map(([key, child]) => [key, walkPartTree(child, atLeaf, [...path, key])]))
    }
}

const partAt = (tree: unknown, path: readonly string[]): unknown => path.reduce((node, key) => (node as Record<string, unknown>)[key], tree)

function mapParts<A extends {}, B extends {}>(tree: MattressGeometryParts<A>, f: (part: A) => B): MattressGeometryParts<B> {
    return walkPartTree(GEOMETRY_PART_TREE, (path) => f(partAt(tree, path) as A)) as MattressGeometryParts<B>
}

function zipParts<R extends Readonly<Record<string, {}>>>(sources: {[K in keyof R]: MattressGeometryParts<R[K]>}): MattressGeometryParts<R> {
    const entries = Object.entries(sources) as [string, unknown][]
    return walkPartTree(GEOMETRY_PART_TREE, (path) => Object.fromEntries(entries.map(([key, tree]) => [key, partAt(tree, path)]))) as MattressGeometryParts<R>
}

function flattenParts<T extends {}>(tree: MattressGeometryParts<T>): T[] {
    const parts: T[] = []
    walkPartTree(GEOMETRY_PART_TREE, (path) => parts.push(partAt(tree, path) as T))
    return parts
}

type MutuallyAssignable<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never

// Compile-time proof that GEOMETRY_PART_TREE and MattressGeometryParts describe the same shape.
// This stops compiling if either one is changed without the other.
const _treeMatchesGeometryParts: MutuallyAssignable<InstantiateTree<typeof GEOMETRY_PART_TREE, symbol>, MattressGeometryParts<symbol>> = true

//////////////////////////////

type MattressTopology = {
    bottom: {
        corner: BottomCornerRadiusInterface
        // square: PuncturedRectangleInterface
        edge: DualRadiusBorderInterface
    }
    sides: SidesInterface
    top: {
        edge: DualRadiusBorderInterface
        corner: TopCornerRadiusInterface
        // square: {},
    }
}

export const mattressTexUVLayout = z
    .literal("texUV_Waterfall")
    .or(z.literal("texUV_InvertedWaterfall"))
    .or(z.literal("texUV_Border"))
    .or(z.literal("texUV_BorderWithMirror"))
    .or(z.literal("texUV_AllOverWrap"))
export type MattressTexUVLayout = z.infer<typeof mattressTexUVLayout>

function mattressTexUVs(
    geometry: MattressGeometryParts<PartAttributes>,
    topology: MattressTopology,
    params: InternalMattressParams,
): Record<MattressTexUVLayout, MattressGeometryParts<AttributeRef>> {
    const transformUV = (uv: AttributeRef, oldOffset: Vec2Literal, newOffset: Vec2Literal, rotation?: number): AttributeRef => {
        let uvMatrix = Matrix3x2.identity().translate({x: newOffset[0], y: newOffset[1]})
        if (rotation) uvMatrix = uvMatrix.rotate(rotation)
        uvMatrix = uvMatrix.translate({x: -oldOffset[0], y: -oldOffset[1]})

        const [u, v] = uv.unpack2()
        return texUVFromBasis(makeShapeBasis(undefined, uvMatrix), u, v)
    }

    const transformQuarterDisc = (
        parts: QuarterDiscParts<AttributeRef>,
        oldOffset: Vec2Literal,
        newOffset: Vec2Literal,
        rotation?: number,
    ): QuarterDiscParts<AttributeRef> => ({
        curvedPatch1: transformUV(parts.curvedPatch1, oldOffset, newOffset, rotation),
        curvedPatch2: transformUV(parts.curvedPatch2, oldOffset, newOffset, rotation),
        flatPatch1: transformUV(parts.flatPatch1, oldOffset, newOffset, rotation),
        flatPatch2: transformUV(parts.flatPatch2, oldOffset, newOffset, rotation),
    })

    const transformUVForBasis = (uv: AttributeRef, basis: Matrix3x2): AttributeRef => {
        const [u, v] = uv.unpack2()
        return texUVFromBasis(makeShapeBasis(undefined, basis), u, v)
    }

    const texUVPatchForDualRadiusQuarterCylPart1 = (
        loopA: AttributeRef,
        loopC: AttributeRef,
        cornerRadius: number,
        edgeRadius: number,
        offset: Vec2Literal = [0, 0],
    ): AttributeRef => {
        let {a1, b1} = Op.disjointProduct(loopC, {b1: loopC}, loopA, {a1: loopA})
        a1 = a1.mul(0.5).sub(0.5)
        b1 = b1.sub(0.5)
        const u1 = a1.mul((cornerRadius * Math.PI) / 2).add(offset[0])
        const v1 = b1.mul((edgeRadius * Math.PI) / 2).add(offset[1])
        const uv1 = texUVFromBasis(makeShapeBasis(), u1, v1)
        return uv1
    }

    const texUVPatchForDualRadiusQuarterCylPart2 = (
        loopB: AttributeRef,
        loopC: AttributeRef,
        cornerRadius: number,
        edgeRadius: number,
        offset: Vec2Literal = [0, 0],
    ): AttributeRef => {
        let {a2, b2} = Op.disjointProduct(loopC, {b2: loopC}, loopB, {a2: loopB})
        a2 = a2.mul(0.5)
        b2 = b2.sub(0.5)
        const u2 = a2.mul((cornerRadius * Math.PI) / 2).add(offset[0])
        const v2 = b2.mul((edgeRadius * Math.PI) / 2).add(offset[1])
        const uv2 = texUVFromBasis(makeShapeBasis(), u2, v2)
        return uv2
    }

    const {width, length, height, topRadius, bottomRadius, cornerRadius} = params

    const topRadiusCircumfrenceAdjust = topRadius * (Math.PI / 2 - 1)
    const bottomRadiusCircumfrenceAdjust = bottomRadius * (Math.PI / 2 - 1)
    const halfCornerCircumfrence = (cornerRadius * Math.PI) / 4

    const innerWidth = width - cornerRadius * 2
    const innerLength = length - cornerRadius * 2

    const defaultTexUVOffset = {
        bottom: {
            edge: {
                top: [0, innerLength / 2 + cornerRadius - bottomRadius + (bottomRadius * Math.PI) / 4],
                right: [-innerWidth / 2 - cornerRadius + bottomRadius - (bottomRadius * Math.PI) / 4, 0],
                left: [innerWidth / 2 + cornerRadius - bottomRadius + (bottomRadius * Math.PI) / 4, 0],
                bottom: [0, -innerLength / 2 - cornerRadius + bottomRadius - (bottomRadius * Math.PI) / 4],
                topLeft: [
                    innerWidth / 2 + (cornerRadius - bottomRadius + (bottomRadius * Math.PI) / 2) / 2,
                    -innerLength / 2 - (cornerRadius - bottomRadius + (bottomRadius * Math.PI) / 2) / 2,
                ],
                topRight: [
                    -innerWidth / 2 - (cornerRadius - bottomRadius + (bottomRadius * Math.PI) / 2) / 2,
                    -innerLength / 2 - (cornerRadius - bottomRadius + (bottomRadius * Math.PI) / 2) / 2,
                ],
                bottomLeft: [
                    innerWidth / 2 + (cornerRadius - bottomRadius + (bottomRadius * Math.PI) / 2) / 2,
                    innerLength / 2 + (cornerRadius - bottomRadius + (bottomRadius * Math.PI) / 2) / 2,
                ],
                bottomRight: [
                    -innerWidth / 2 - (cornerRadius - bottomRadius + (bottomRadius * Math.PI) / 2) / 2,
                    innerLength / 2 + (cornerRadius - bottomRadius + (bottomRadius * Math.PI) / 2) / 2,
                ],
            },
            corner: {
                top: [0, innerLength / 2 + (cornerRadius - bottomRadius) / 2],
                right: [-innerWidth / 2 - (cornerRadius - bottomRadius) / 2, 0],
                left: [innerWidth / 2 + (cornerRadius - bottomRadius) / 2, 0],
                bottom: [0, -innerLength / 2 - (cornerRadius - bottomRadius) / 2],
            },
            square: {
                top: [0, innerLength / 2],
                left: [-innerWidth / 2, 0],
                right: [innerWidth / 2, 0],
                bottom: [0, -innerLength / 2],
            },
        },
        top: {
            edge: {
                top: [0, innerLength / 2 + cornerRadius - topRadius + (topRadius * Math.PI) / 4],
                right: [innerWidth / 2 + cornerRadius - topRadius + (topRadius * Math.PI) / 4, 0],
                left: [-innerWidth / 2 - cornerRadius + topRadius - (topRadius * Math.PI) / 4, 0],
                bottom: [0, -innerLength / 2 - cornerRadius + topRadius - (topRadius * Math.PI) / 4],
            },
            corner: {
                top: [0, innerLength / 2 + (cornerRadius - topRadius) / 2],
                right: [innerWidth / 2 + (cornerRadius - topRadius) / 2, 0],
                left: [-innerWidth / 2 - (cornerRadius - topRadius) / 2, 0],
                bottom: [0, -innerLength / 2 - (cornerRadius - topRadius) / 2],
            },
            square: {
                top: [0, innerLength / 2],
                left: [-innerWidth / 2, 0],
                right: [innerWidth / 2, 0],
                bottom: [0, -innerLength / 2],
            },
        },
        sides: {
            top: {
                halfCorner1: [-innerWidth / 2 - halfCornerCircumfrence / 2, length / 2 + topRadiusCircumfrenceAdjust + height / 2],
                side: [0, length / 2 + topRadiusCircumfrenceAdjust + height / 2],
                halfCorner2: [innerWidth / 2 + halfCornerCircumfrence / 2, length / 2 + topRadiusCircumfrenceAdjust + height / 2],
            },
            bottom: {
                halfCorner1: [innerWidth / 2 + halfCornerCircumfrence / 2, -length / 2 - topRadiusCircumfrenceAdjust - height / 2],
                side: [0, -length / 2 - topRadiusCircumfrenceAdjust - height / 2],
                halfCorner2: [-innerWidth / 2 - halfCornerCircumfrence / 2, -length / 2 - topRadiusCircumfrenceAdjust - height / 2],
            },
            left: {
                halfCorner1: [-width / 2 - topRadiusCircumfrenceAdjust - height / 2, -innerLength / 2 - halfCornerCircumfrence / 2],
                side: [-width / 2 - topRadiusCircumfrenceAdjust - height / 2, 0],
                halfCorner2: [-width / 2 - topRadiusCircumfrenceAdjust - height / 2, innerLength / 2 + halfCornerCircumfrence / 2],
            },
            right: {
                halfCorner1: [width / 2 + topRadiusCircumfrenceAdjust + height / 2, innerLength / 2 + halfCornerCircumfrence / 2],
                side: [width / 2 + topRadiusCircumfrenceAdjust + height / 2, 0],
                halfCorner2: [width / 2 + topRadiusCircumfrenceAdjust + height / 2, -innerLength / 2 - halfCornerCircumfrence / 2],
            },
        },
    } as const

    const texUV_Waterfall = (): MattressGeometryParts<AttributeRef> => mapParts(geometry, (part) => part.texUV)

    const texUV_InvertedWaterfall = (): MattressGeometryParts<AttributeRef> => {
        const vOffsetTop = length / 2 + bottomRadiusCircumfrenceAdjust + height / 2
        const vOffsetBottom = -vOffsetTop
        const uOffsetLeft = width / 2 + bottomRadiusCircumfrenceAdjust + height / 2
        const uOffsetRight = -uOffsetLeft

        return {
            ...texUV_Waterfall(),
            sides: {
                bottom: {
                    halfCorner1: transformUV(
                        geometry.sides.bottom.halfCorner1.texUV,
                        defaultTexUVOffset.sides.bottom.halfCorner1,
                        [-defaultTexUVOffset.sides.bottom.halfCorner1[0], vOffsetBottom],
                        180,
                    ),
                    side: transformUV(
                        geometry.sides.bottom.side.texUV,
                        defaultTexUVOffset.sides.bottom.side,
                        [-defaultTexUVOffset.sides.bottom.side[0], vOffsetBottom],
                        180,
                    ),
                    halfCorner2: transformUV(
                        geometry.sides.bottom.halfCorner2.texUV,
                        defaultTexUVOffset.sides.bottom.halfCorner2,
                        [-defaultTexUVOffset.sides.bottom.halfCorner2[0], vOffsetBottom],
                        180,
                    ),
                },
                left: {
                    halfCorner1: transformUV(geometry.sides.left.halfCorner1.texUV, defaultTexUVOffset.sides.left.halfCorner1, [
                        uOffsetLeft,
                        defaultTexUVOffset.sides.left.halfCorner1[1],
                    ]),
                    side: transformUV(geometry.sides.left.side.texUV, defaultTexUVOffset.sides.left.side, [uOffsetLeft, defaultTexUVOffset.sides.left.side[1]]),
                    halfCorner2: transformUV(geometry.sides.left.halfCorner2.texUV, defaultTexUVOffset.sides.left.halfCorner2, [
                        uOffsetLeft,
                        defaultTexUVOffset.sides.left.halfCorner2[1],
                    ]),
                },
                right: {
                    halfCorner1: transformUV(geometry.sides.right.halfCorner1.texUV, defaultTexUVOffset.sides.right.halfCorner1, [
                        uOffsetRight,
                        defaultTexUVOffset.sides.right.halfCorner1[1],
                    ]),
                    side: transformUV(geometry.sides.right.side.texUV, defaultTexUVOffset.sides.right.side, [
                        uOffsetRight,
                        defaultTexUVOffset.sides.right.side[1],
                    ]),
                    halfCorner2: transformUV(geometry.sides.right.halfCorner2.texUV, defaultTexUVOffset.sides.right.halfCorner2, [
                        uOffsetRight,
                        defaultTexUVOffset.sides.right.halfCorner2[1],
                    ]),
                },
                top: {
                    halfCorner1: transformUV(
                        geometry.sides.top.halfCorner1.texUV,
                        defaultTexUVOffset.sides.top.halfCorner1,
                        [-defaultTexUVOffset.sides.top.halfCorner1[0], vOffsetTop],
                        180,
                    ),
                    side: transformUV(
                        geometry.sides.top.side.texUV,
                        defaultTexUVOffset.sides.top.side,
                        [-defaultTexUVOffset.sides.top.side[0], vOffsetTop],
                        180,
                    ),
                    halfCorner2: transformUV(
                        geometry.sides.top.halfCorner2.texUV,
                        defaultTexUVOffset.sides.top.halfCorner2,
                        [-defaultTexUVOffset.sides.top.halfCorner2[0], vOffsetTop],
                        180,
                    ),
                },
            },
        }
    }

    const texUV_BorderSideParts = (spacingBetweenSides: number = 0): MattressGeometryParts<AttributeRef>["sides"] => {
        // sides parts:
        // right side
        // right halfCorner1
        // top halfCorner2
        // top side
        // top halfCorner1
        // left halfCorner2
        // left side
        // left halfCorner1
        // bottom halfCorner2
        // bottom side
        // bottom halfCorner1
        // right halfCorner2

        const uOffsetRightHalfCorner2 = halfCornerCircumfrence / 2
        const uOffsetRightSide = uOffsetRightHalfCorner2 + innerLength / 2 + halfCornerCircumfrence / 2
        const uOffsetRightHalfCorner1 = uOffsetRightSide + innerLength / 2 + halfCornerCircumfrence / 2
        const uOffsetTopHalfCorner2 = uOffsetRightHalfCorner1 + halfCornerCircumfrence + spacingBetweenSides
        const uOffsetTopSide = uOffsetTopHalfCorner2 + halfCornerCircumfrence / 2 + innerWidth / 2
        const uOffsetTopHalfCorner1 = uOffsetTopSide + innerWidth / 2 + halfCornerCircumfrence / 2
        const uOffsetLeftHalfCorner2 = uOffsetTopHalfCorner1 + halfCornerCircumfrence + spacingBetweenSides
        const uOffsetLeftSide = uOffsetLeftHalfCorner2 + halfCornerCircumfrence / 2 + innerLength / 2
        const uOffsetLeftHalfCorner1 = uOffsetLeftSide + innerLength / 2 + halfCornerCircumfrence / 2
        const uOffsetBottomHalfCorner2 = uOffsetLeftHalfCorner1 + halfCornerCircumfrence + spacingBetweenSides
        const uOffsetBottomSide = uOffsetBottomHalfCorner2 + halfCornerCircumfrence / 2 + innerWidth / 2
        const uOffsetBottomHalfCorner1 = uOffsetBottomSide + innerWidth / 2 + halfCornerCircumfrence / 2

        return {
            bottom: {
                halfCorner1: transformUV(geometry.sides.bottom.halfCorner1.texUV, defaultTexUVOffset.sides.bottom.halfCorner1, [uOffsetBottomHalfCorner1, 0]),
                side: transformUV(geometry.sides.bottom.side.texUV, defaultTexUVOffset.sides.bottom.side, [uOffsetBottomSide, 0]),
                halfCorner2: transformUV(geometry.sides.bottom.halfCorner2.texUV, defaultTexUVOffset.sides.bottom.halfCorner2, [uOffsetBottomHalfCorner2, 0]),
            },
            left: {
                halfCorner1: transformUV(geometry.sides.left.halfCorner1.texUV, defaultTexUVOffset.sides.left.halfCorner1, [uOffsetLeftHalfCorner1, 0], 90),
                side: transformUV(geometry.sides.left.side.texUV, defaultTexUVOffset.sides.left.side, [uOffsetLeftSide, 0], 90),
                halfCorner2: transformUV(geometry.sides.left.halfCorner2.texUV, defaultTexUVOffset.sides.left.halfCorner2, [uOffsetLeftHalfCorner2, 0], 90),
            },
            right: {
                halfCorner1: transformUV(geometry.sides.right.halfCorner1.texUV, defaultTexUVOffset.sides.right.halfCorner1, [uOffsetRightHalfCorner1, 0], -90),
                side: transformUV(geometry.sides.right.side.texUV, defaultTexUVOffset.sides.right.side, [uOffsetRightSide, 0], -90),
                halfCorner2: transformUV(geometry.sides.right.halfCorner2.texUV, defaultTexUVOffset.sides.right.halfCorner2, [uOffsetRightHalfCorner2, 0], -90),
            },
            top: {
                halfCorner1: transformUV(geometry.sides.top.halfCorner1.texUV, defaultTexUVOffset.sides.top.halfCorner1, [uOffsetTopHalfCorner1, 0], 180),
                side: transformUV(geometry.sides.top.side.texUV, defaultTexUVOffset.sides.top.side, [uOffsetTopSide, 0], 180),
                halfCorner2: transformUV(geometry.sides.top.halfCorner2.texUV, defaultTexUVOffset.sides.top.halfCorner2, [uOffsetTopHalfCorner2, 0], 180),
            },
        }
    }

    const texUV_BorderEdgeParts = (
        spacingBetweenEdges: number = 0,
    ): {topEdge: MattressGeometryParts<AttributeRef>["top"]["edge"]; bottomEdge: MattressGeometryParts<AttributeRef>["bottom"]["edge"]} => {
        // bottom and top edges
        const vOffsetBottomEdge = -height / 2 - (bottomRadius * Math.PI) / 4
        const vOffsetTopEdge = height / 2 + (topRadius * Math.PI) / 4

        const uOffsetEdgeBottomRightPart1 = halfCornerCircumfrence / 2
        const uOffsetEdgeRight = uOffsetEdgeBottomRightPart1 + innerLength / 2 + halfCornerCircumfrence / 2
        const uOffsetEdgeTopRight = uOffsetEdgeRight + innerLength / 2 + halfCornerCircumfrence + spacingBetweenEdges / 2
        const uOffsetEdgeTop = uOffsetEdgeTopRight + halfCornerCircumfrence + innerWidth / 2 + spacingBetweenEdges / 2
        const uOffsetEdgeTopLeft = uOffsetEdgeTop + innerWidth / 2 + (cornerRadius * Math.PI) / 4 + spacingBetweenEdges / 2
        const uOffsetEdgeLeft = uOffsetEdgeTopLeft + halfCornerCircumfrence + innerLength / 2 + spacingBetweenEdges / 2
        const uOffsetEdgeBottomLeft = uOffsetEdgeLeft + innerLength / 2 + halfCornerCircumfrence + spacingBetweenEdges / 2
        const uOffsetEdgeBottom = uOffsetEdgeBottomLeft + halfCornerCircumfrence + innerWidth / 2 + spacingBetweenEdges / 2
        const uOffsetEdgeBottomRightPart2 = uOffsetEdgeBottom + innerWidth / 2 + halfCornerCircumfrence / 2

        const texUV_EdgeCornerPart = (
            loopA: AttributeRef,
            loopB: AttributeRef,
            loopC: AttributeRef,
            cornerRadius: number,
            edgeRadius: number,
            uSpacingBetweenSubParts: number = 0,
        ): AttributeRef => {
            const uv1 = texUVPatchForDualRadiusQuarterCylPart1(loopA, loopC, cornerRadius, edgeRadius, [-uSpacingBetweenSubParts / 2, 0])
            const uv2 = texUVPatchForDualRadiusQuarterCylPart2(loopB, loopC, cornerRadius, edgeRadius, [uSpacingBetweenSubParts / 2, 0])
            return Op.merge(uv1, uv2)
        }

        return {
            bottomEdge: {
                rightTop: transformUV(geometry.bottom.edge.rightTop.texUV, defaultTexUVOffset.bottom.edge.right, [uOffsetEdgeRight, vOffsetBottomEdge], -90),
                rightBottom: transformUV(
                    geometry.bottom.edge.rightBottom.texUV,
                    defaultTexUVOffset.bottom.edge.right,
                    [uOffsetEdgeRight, vOffsetBottomEdge],
                    -90,
                ),
                topRight: transformUV(
                    texUV_EdgeCornerPart(
                        topology.bottom.edge.loops[4],
                        flipLoop(topology.bottom.edge.loops[1]),
                        topology.bottom.edge.innerLoop,
                        cornerRadius,
                        bottomRadius,
                        spacingBetweenEdges,
                    ),
                    [0, 0],
                    [uOffsetEdgeTopRight, vOffsetBottomEdge],
                    180,
                ),
                top: transformUV(geometry.bottom.edge.top.texUV, defaultTexUVOffset.bottom.edge.top, [uOffsetEdgeTop, vOffsetBottomEdge]),
                topLeft: transformUV(
                    texUV_EdgeCornerPart(
                        topology.bottom.edge.loops[1],
                        topology.bottom.edge.loops[2],
                        topology.bottom.edge.innerLoop,
                        cornerRadius,
                        bottomRadius,
                        spacingBetweenEdges,
                    ),
                    [0, 0],
                    [uOffsetEdgeTopLeft, vOffsetBottomEdge],
                    180,
                ),
                leftTop: transformUV(geometry.bottom.edge.leftTop.texUV, defaultTexUVOffset.bottom.edge.left, [uOffsetEdgeLeft, vOffsetBottomEdge], 90),
                leftBottom: transformUV(geometry.bottom.edge.leftBottom.texUV, defaultTexUVOffset.bottom.edge.left, [uOffsetEdgeLeft, vOffsetBottomEdge], 90),
                bottomLeft: transformUV(
                    texUV_EdgeCornerPart(
                        flipLoop(topology.bottom.edge.loops[4]),
                        topology.bottom.edge.loops[5],
                        topology.bottom.edge.innerLoop,
                        cornerRadius,
                        bottomRadius,
                        spacingBetweenEdges,
                    ),
                    [0, 0],
                    [uOffsetEdgeBottomLeft, vOffsetBottomEdge],
                    180,
                ),
                bottom: transformUV(geometry.bottom.edge.bottom.texUV, defaultTexUVOffset.bottom.edge.bottom, [uOffsetEdgeBottom, vOffsetBottomEdge], -180),
                bottomRight: Op.merge(
                    transformUV(
                        texUVPatchForDualRadiusQuarterCylPart1(topology.bottom.edge.loops[5], topology.bottom.edge.innerLoop, cornerRadius, bottomRadius),
                        [-halfCornerCircumfrence / 2, 0],
                        [uOffsetEdgeBottomRightPart1, vOffsetBottomEdge],
                        180,
                    ),
                    transformUV(
                        texUVPatchForDualRadiusQuarterCylPart2(
                            flipLoop(topology.bottom.edge.loops[4]),
                            topology.bottom.edge.innerLoop,
                            cornerRadius,
                            bottomRadius,
                        ),
                        [halfCornerCircumfrence / 2, 0],
                        [uOffsetEdgeBottomRightPart2, vOffsetBottomEdge],
                        180,
                    ),
                ),
            },
            topEdge: {
                rightTop: transformUV(geometry.top.edge.rightTop.texUV, defaultTexUVOffset.top.edge.right, [uOffsetEdgeRight, vOffsetTopEdge], -90),
                rightBottom: transformUV(geometry.top.edge.rightBottom.texUV, defaultTexUVOffset.top.edge.right, [uOffsetEdgeRight, vOffsetTopEdge], -90),
                topRight: transformUV(
                    texUV_EdgeCornerPart(
                        topology.top.edge.loops[4],
                        flipLoop(topology.top.edge.loops[1]),
                        topology.top.edge.innerLoop,
                        -cornerRadius, // inverted radius
                        topRadius,
                        -spacingBetweenEdges,
                    ),
                    [0, 0],
                    [uOffsetEdgeTopRight, vOffsetTopEdge],
                ),
                top: transformUV(geometry.top.edge.top.texUV, defaultTexUVOffset.top.edge.top, [uOffsetEdgeTop, vOffsetTopEdge], -180),
                topLeft: transformUV(
                    texUV_EdgeCornerPart(
                        topology.top.edge.loops[1],
                        topology.top.edge.loops[2],
                        topology.top.edge.innerLoop,
                        -cornerRadius, // inverted radius
                        topRadius,
                        -spacingBetweenEdges,
                    ),
                    [0, 0],
                    [uOffsetEdgeTopLeft, vOffsetTopEdge],
                ),
                leftTop: transformUV(geometry.top.edge.leftTop.texUV, defaultTexUVOffset.top.edge.left, [uOffsetEdgeLeft, vOffsetTopEdge], 90),
                leftBottom: transformUV(geometry.top.edge.leftBottom.texUV, defaultTexUVOffset.top.edge.left, [uOffsetEdgeLeft, vOffsetTopEdge], 90),
                bottomLeft: transformUV(
                    texUV_EdgeCornerPart(
                        flipLoop(topology.top.edge.loops[4]),
                        topology.top.edge.loops[5],
                        topology.top.edge.innerLoop,
                        -cornerRadius, // inverted radius
                        topRadius,
                        -spacingBetweenEdges,
                    ),
                    [0, 0],
                    [uOffsetEdgeBottomLeft, vOffsetTopEdge],
                ),
                bottom: transformUV(geometry.top.edge.bottom.texUV, defaultTexUVOffset.top.edge.bottom, [uOffsetEdgeBottom, vOffsetTopEdge]),
                bottomRight: Op.merge(
                    transformUV(
                        texUVPatchForDualRadiusQuarterCylPart1(
                            topology.top.edge.loops[5],
                            topology.top.edge.innerLoop,
                            -cornerRadius, //inverted radius
                            topRadius,
                        ),
                        [halfCornerCircumfrence / 2, 0],
                        [uOffsetEdgeBottomRightPart1, vOffsetTopEdge],
                    ),
                    transformUV(
                        texUVPatchForDualRadiusQuarterCylPart2(
                            flipLoop(topology.top.edge.loops[4]),
                            topology.top.edge.innerLoop,
                            -cornerRadius, //inverted radius
                            topRadius,
                        ),
                        [-halfCornerCircumfrence / 2, 0],
                        [uOffsetEdgeBottomRightPart2, vOffsetTopEdge],
                    ),
                ),
            },
        }
    }

    const texUV_Border = (): MattressGeometryParts<AttributeRef> => {
        const texUV_default = texUV_Waterfall()
        const {bottomEdge, topEdge} = texUV_BorderEdgeParts()

        return {
            sides: texUV_BorderSideParts(),
            bottom: {
                square: texUV_default.bottom.square,
                corner: texUV_default.bottom.corner,
                edge: bottomEdge,
            },
            top: {
                square: texUV_default.top.square,
                corner: texUV_default.top.corner,
                edge: topEdge,
            },
        }
    }

    const texUV_BorderWithMirror = (): MattressGeometryParts<AttributeRef> => {
        const spacing = 10
        const texUV_default = texUV_Border()
        const {bottomEdge, topEdge} = texUV_BorderEdgeParts(spacing)

        // bottom and top corner
        // start at bottom right, go counter clockwise
        const vOffsetBottomCorner = -height / 2 - (bottomRadius * Math.PI) / 2 - (cornerRadius - bottomRadius) / 2
        const vOffsetTopCorner = height / 2 + (topRadius * Math.PI) / 2 + (cornerRadius - topRadius) / 2

        const vOffsetBottomSquare = vOffsetBottomCorner - (cornerRadius - bottomRadius) / 2
        const vOffsetTopSquare = vOffsetTopCorner + (cornerRadius - topRadius) / 2

        const uOffsetCornerRight = halfCornerCircumfrence + innerLength / 2
        const uOffsetCornerTop = uOffsetCornerRight + innerLength / 2 + 2 * halfCornerCircumfrence + innerWidth / 2 + spacing
        const uOffsetCornerLeft = uOffsetCornerTop + innerWidth / 2 + 2 * halfCornerCircumfrence + innerLength / 2 + spacing
        const uOffsetCornerBottom = uOffsetCornerLeft + innerLength / 2 + 2 * halfCornerCircumfrence + innerWidth / 2 + spacing

        const texUV_cornerCurvedPatch = (loopA: AttributeRef, loopB: AttributeRef, cornerRadius: number, edgeRadius: number): AttributeRef => {
            const {a, b} = Op.disjointProduct(loopA, {a: loopA}, loopB, {b: loopB})
            return bilinear(
                a,
                b.oneMinus(),
                [0, 0],
                [(cornerRadius * Math.PI) / 4, 0],
                [0, (cornerRadius - edgeRadius) / 2],
                [(cornerRadius * Math.PI) / 4, (cornerRadius - edgeRadius) / 2],
            )
        }

        const texUV_cornerFlatPatches = (
            loopA: AttributeRef,
            loopB: AttributeRef,
            cornerRadius: number,
            edgeRadius: number,
            basisPatch1: Matrix3x2,
            basisPatch2: Matrix3x2,
        ): {flatPatch1: AttributeRef; flatPatch2: AttributeRef} => {
            const {patch1, patch2} = quarterDiscFlatPatches(loopA, loopB, [0, 0], [0, 1], [1, 0], [1, 1], 0.5)

            return {
                flatPatch1: transformUVForBasis(
                    // NOTE: additional factor 0.5 used to re-scale back to unit triangle (see xy scaling in )
                    transformUVForBasis(patch1.xy, Matrix3x2.fromArray([0, -(cornerRadius - edgeRadius), (cornerRadius * Math.PI) / 2, 0, 0, 0])),
                    basisPatch1,
                ),
                flatPatch2: transformUVForBasis(
                    // NOTE: additional factor 0.5 used to re-scale back to unit triangle (see xy scaling in )
                    transformUVForBasis(patch2.xy, Matrix3x2.fromArray([-(cornerRadius * Math.PI) / 2, 0, 0, -(cornerRadius - edgeRadius), 0, 0])),
                    basisPatch2,
                ),
            }
        }

        return {
            sides: texUV_BorderSideParts(spacing),
            bottom: {
                square: {
                    top: transformUV(texUV_default.bottom.square.top, defaultTexUVOffset.bottom.square.top, [uOffsetCornerTop, vOffsetBottomSquare]),
                    leftTop: transformUV(
                        texUV_default.bottom.square.leftTop,
                        defaultTexUVOffset.bottom.square.left,
                        [uOffsetCornerRight, vOffsetBottomSquare],
                        -90,
                    ),
                    leftBottom: transformUV(
                        texUV_default.bottom.square.leftBottom,
                        defaultTexUVOffset.bottom.square.left,
                        [uOffsetCornerRight, vOffsetBottomSquare],
                        -90,
                    ),
                    bottom: transformUV(
                        texUV_default.bottom.square.bottom,
                        defaultTexUVOffset.bottom.square.bottom,
                        [uOffsetCornerBottom, vOffsetBottomSquare],
                        180,
                    ),
                    rightTop: transformUV(
                        texUV_default.bottom.square.rightTop,
                        defaultTexUVOffset.bottom.square.right,
                        [uOffsetCornerLeft, vOffsetBottomSquare],
                        90,
                    ),
                    rightBottom: transformUV(
                        texUV_default.bottom.square.rightBottom,
                        defaultTexUVOffset.bottom.square.right,
                        [uOffsetCornerLeft, vOffsetBottomSquare],
                        90,
                    ),
                },
                corner: {
                    topRight: {
                        curvedPatch1: transformUVForBasis(
                            texUV_cornerCurvedPatch(topology.bottom.edge.loops[4], topology.bottom.corner.innerLoop, cornerRadius, bottomRadius),
                            Matrix3x2.fromArray([-1, 0, 0, -1, uOffsetCornerTop - innerWidth / 2, vOffsetBottomCorner + (cornerRadius - bottomRadius) / 2]),
                        ),
                        curvedPatch2: transformUVForBasis(
                            texUV_cornerCurvedPatch(flipLoop(topology.bottom.edge.loops[1]), topology.bottom.corner.innerLoop, cornerRadius, bottomRadius),
                            Matrix3x2.fromArray([1, 0, 0, -1, uOffsetCornerRight + innerLength / 2, vOffsetBottomCorner + (cornerRadius - bottomRadius) / 2]),
                        ),
                        ...texUV_cornerFlatPatches(
                            topology.bottom.edge.loops[4],
                            flipLoop(topology.bottom.edge.loops[1]),
                            cornerRadius,
                            bottomRadius,
                            Matrix3x2.fromArray([1, 0, 0, -1, uOffsetCornerRight + innerLength / 2, vOffsetBottomCorner - (cornerRadius - bottomRadius) / 2]),
                            Matrix3x2.fromArray([1, 0, 0, -1, uOffsetCornerTop - innerWidth / 2, vOffsetBottomCorner - (cornerRadius - bottomRadius) / 2]),
                        ),
                    },
                    topLeft: {
                        curvedPatch1: transformUVForBasis(
                            texUV_cornerCurvedPatch(topology.bottom.edge.loops[1], topology.bottom.corner.innerLoop, cornerRadius, bottomRadius),
                            Matrix3x2.fromArray([-1, 0, 0, -1, uOffsetCornerLeft - innerLength / 2, vOffsetBottomCorner + (cornerRadius - bottomRadius) / 2]),
                        ),
                        curvedPatch2: transformUVForBasis(
                            texUV_cornerCurvedPatch(topology.bottom.edge.loops[2], topology.bottom.corner.innerLoop, cornerRadius, bottomRadius),
                            Matrix3x2.fromArray([1, 0, 0, -1, uOffsetCornerTop + innerWidth / 2, vOffsetBottomCorner + (cornerRadius - bottomRadius) / 2]),
                        ),
                        ...texUV_cornerFlatPatches(
                            topology.bottom.edge.loops[1],
                            topology.bottom.edge.loops[2],
                            cornerRadius,
                            bottomRadius,
                            Matrix3x2.fromArray([1, 0, 0, -1, uOffsetCornerTop + innerWidth / 2, vOffsetBottomCorner - (cornerRadius - bottomRadius) / 2]),
                            Matrix3x2.fromArray([1, 0, 0, -1, uOffsetCornerLeft - innerLength / 2, vOffsetBottomCorner - (cornerRadius - bottomRadius) / 2]),
                        ),
                    },
                    bottomLeft: {
                        curvedPatch1: transformUVForBasis(
                            texUV_cornerCurvedPatch(flipLoop(topology.bottom.edge.loops[2]), topology.bottom.corner.innerLoop, cornerRadius, bottomRadius),
                            Matrix3x2.fromArray([-1, 0, 0, -1, uOffsetCornerBottom - innerWidth / 2, vOffsetBottomCorner + (cornerRadius - bottomRadius) / 2]),
                        ),
                        curvedPatch2: transformUVForBasis(
                            texUV_cornerCurvedPatch(topology.bottom.edge.loops[5], topology.bottom.corner.innerLoop, cornerRadius, bottomRadius),
                            Matrix3x2.fromArray([1, 0, 0, -1, uOffsetCornerLeft + innerLength / 2, vOffsetBottomCorner + (cornerRadius - bottomRadius) / 2]),
                        ),
                        ...texUV_cornerFlatPatches(
                            flipLoop(topology.bottom.edge.loops[2]),
                            topology.bottom.edge.loops[5],
                            cornerRadius,
                            bottomRadius,
                            Matrix3x2.fromArray([1, 0, 0, -1, uOffsetCornerLeft + innerLength / 2, vOffsetBottomCorner - (cornerRadius - bottomRadius) / 2]),
                            Matrix3x2.fromArray([1, 0, 0, -1, uOffsetCornerBottom - innerWidth / 2, vOffsetBottomCorner - (cornerRadius - bottomRadius) / 2]),
                        ),
                    },
                    bottomRight: {
                        curvedPatch1: transformUVForBasis(
                            texUV_cornerCurvedPatch(topology.bottom.edge.loops[5], topology.bottom.corner.innerLoop, cornerRadius, bottomRadius),
                            Matrix3x2.fromArray([-1, 0, 0, -1, uOffsetCornerRight - innerLength / 2, vOffsetBottomCorner + (cornerRadius - bottomRadius) / 2]),
                        ),
                        curvedPatch2: transformUVForBasis(
                            texUV_cornerCurvedPatch(flipLoop(topology.bottom.edge.loops[4]), topology.bottom.corner.innerLoop, cornerRadius, bottomRadius),
                            Matrix3x2.fromArray([1, 0, 0, -1, uOffsetCornerBottom + innerWidth / 2, vOffsetBottomCorner + (cornerRadius - bottomRadius) / 2]),
                        ),
                        ...texUV_cornerFlatPatches(
                            topology.bottom.edge.loops[5],
                            flipLoop(topology.bottom.edge.loops[4]),
                            cornerRadius,
                            bottomRadius,
                            Matrix3x2.fromArray([1, 0, 0, -1, uOffsetCornerBottom + innerWidth / 2, vOffsetBottomCorner - (cornerRadius - bottomRadius) / 2]),
                            Matrix3x2.fromArray([1, 0, 0, -1, uOffsetCornerRight - innerLength / 2, vOffsetBottomCorner - (cornerRadius - bottomRadius) / 2]),
                        ),
                    },
                    leftTop: transformUV(
                        texUV_default.bottom.corner.leftTop,
                        defaultTexUVOffset.bottom.corner.left,
                        [uOffsetCornerLeft, vOffsetBottomCorner],
                        90,
                    ),
                    leftBottom: transformUV(
                        texUV_default.bottom.corner.leftBottom,
                        defaultTexUVOffset.bottom.corner.left,
                        [uOffsetCornerLeft, vOffsetBottomCorner],
                        90,
                    ),
                    rightTop: transformUV(
                        texUV_default.bottom.corner.rightTop,
                        defaultTexUVOffset.bottom.corner.right,
                        [uOffsetCornerRight, vOffsetBottomCorner],
                        -90,
                    ),
                    rightBottom: transformUV(
                        texUV_default.bottom.corner.rightBottom,
                        defaultTexUVOffset.bottom.corner.right,
                        [uOffsetCornerRight, vOffsetBottomCorner],
                        -90,
                    ),
                    top: transformUV(texUV_default.bottom.corner.top, defaultTexUVOffset.bottom.corner.top, [uOffsetCornerTop, vOffsetBottomCorner], 0),
                    bottom: transformUV(
                        texUV_default.bottom.corner.bottom,
                        defaultTexUVOffset.bottom.corner.bottom,
                        [uOffsetCornerBottom, vOffsetBottomCorner],
                        180,
                    ),
                },
                edge: bottomEdge,
            },
            top: {
                square: {
                    top: transformUV(texUV_default.top.square.top, defaultTexUVOffset.top.square.top, [uOffsetCornerTop, vOffsetTopSquare], 180),
                    leftTop: transformUV(texUV_default.top.square.leftTop, defaultTexUVOffset.top.square.left, [uOffsetCornerLeft, vOffsetTopSquare], 90),
                    leftBottom: transformUV(texUV_default.top.square.leftBottom, defaultTexUVOffset.top.square.left, [uOffsetCornerLeft, vOffsetTopSquare], 90),
                    bottom: transformUV(texUV_default.top.square.bottom, defaultTexUVOffset.top.square.bottom, [uOffsetCornerBottom, vOffsetTopSquare], 0),
                    rightTop: transformUV(texUV_default.top.square.rightTop, defaultTexUVOffset.top.square.right, [uOffsetCornerRight, vOffsetTopSquare], -90),
                    rightBottom: transformUV(
                        texUV_default.top.square.rightBottom,
                        defaultTexUVOffset.top.square.right,
                        [uOffsetCornerRight, vOffsetTopSquare],
                        -90,
                    ),
                },
                corner: {
                    topRight: {
                        curvedPatch1: transformUVForBasis(
                            texUV_cornerCurvedPatch(topology.top.edge.loops[4], topology.top.corner.innerLoop, cornerRadius, topRadius),
                            Matrix3x2.fromArray([-1, 0, 0, 1, uOffsetCornerTop - innerWidth / 2, vOffsetTopCorner - (cornerRadius - topRadius) / 2]),
                        ),
                        curvedPatch2: transformUVForBasis(
                            texUV_cornerCurvedPatch(flipLoop(topology.top.edge.loops[1]), topology.top.corner.innerLoop, cornerRadius, topRadius),
                            Matrix3x2.fromArray([1, 0, 0, 1, uOffsetCornerRight + innerLength / 2, vOffsetTopCorner - (cornerRadius - topRadius) / 2]),
                        ),
                        ...texUV_cornerFlatPatches(
                            topology.top.edge.loops[4],
                            flipLoop(topology.top.edge.loops[1]),
                            cornerRadius,
                            topRadius,
                            Matrix3x2.fromArray([1, 0, 0, 1, uOffsetCornerRight + innerLength / 2, vOffsetTopCorner + (cornerRadius - topRadius) / 2]),
                            Matrix3x2.fromArray([1, 0, 0, 1, uOffsetCornerTop - innerWidth / 2, vOffsetTopCorner + (cornerRadius - topRadius) / 2]),
                        ),
                    },
                    topLeft: {
                        curvedPatch1: transformUVForBasis(
                            texUV_cornerCurvedPatch(topology.top.edge.loops[1], topology.top.corner.innerLoop, cornerRadius, topRadius),
                            Matrix3x2.fromArray([-1, 0, 0, 1, uOffsetCornerLeft - innerLength / 2, vOffsetTopCorner - (cornerRadius - topRadius) / 2]),
                        ),
                        curvedPatch2: transformUVForBasis(
                            texUV_cornerCurvedPatch(topology.top.edge.loops[2], topology.top.corner.innerLoop, cornerRadius, topRadius),
                            Matrix3x2.fromArray([1, 0, 0, 1, uOffsetCornerTop + innerWidth / 2, vOffsetTopCorner - (cornerRadius - topRadius) / 2]),
                        ),
                        ...texUV_cornerFlatPatches(
                            topology.top.edge.loops[1],
                            topology.top.edge.loops[2],
                            cornerRadius,
                            topRadius,
                            Matrix3x2.fromArray([1, 0, 0, 1, uOffsetCornerTop + innerWidth / 2, vOffsetTopCorner + (cornerRadius - topRadius) / 2]),
                            Matrix3x2.fromArray([1, 0, 0, 1, uOffsetCornerLeft - innerLength / 2, vOffsetTopCorner + (cornerRadius - topRadius) / 2]),
                        ),
                    },

                    bottomLeft: {
                        curvedPatch1: transformUVForBasis(
                            texUV_cornerCurvedPatch(flipLoop(topology.top.edge.loops[2]), topology.top.corner.innerLoop, cornerRadius, topRadius),
                            Matrix3x2.fromArray([-1, 0, 0, 1, uOffsetCornerBottom - innerWidth / 2, vOffsetTopCorner - (cornerRadius - topRadius) / 2]),
                        ),
                        curvedPatch2: transformUVForBasis(
                            texUV_cornerCurvedPatch(topology.top.edge.loops[5], topology.top.corner.innerLoop, cornerRadius, topRadius),
                            Matrix3x2.fromArray([1, 0, 0, 1, uOffsetCornerLeft + innerLength / 2, vOffsetTopCorner - (cornerRadius - topRadius) / 2]),
                        ),
                        ...texUV_cornerFlatPatches(
                            flipLoop(topology.top.edge.loops[2]),
                            topology.top.edge.loops[5],
                            cornerRadius,
                            topRadius,
                            Matrix3x2.fromArray([1, 0, 0, 1, uOffsetCornerLeft + innerLength / 2, vOffsetTopCorner + (cornerRadius - topRadius) / 2]),
                            Matrix3x2.fromArray([1, 0, 0, 1, uOffsetCornerBottom - innerWidth / 2, vOffsetTopCorner + (cornerRadius - topRadius) / 2]),
                        ),
                    },
                    bottomRight: {
                        curvedPatch1: transformUVForBasis(
                            texUV_cornerCurvedPatch(topology.top.edge.loops[5], topology.top.corner.innerLoop, cornerRadius, topRadius),
                            Matrix3x2.fromArray([-1, 0, 0, 1, uOffsetCornerRight - innerLength / 2, vOffsetTopCorner - (cornerRadius - topRadius) / 2]),
                        ),
                        curvedPatch2: transformUVForBasis(
                            texUV_cornerCurvedPatch(flipLoop(topology.top.edge.loops[4]), topology.top.corner.innerLoop, cornerRadius, topRadius),
                            Matrix3x2.fromArray([1, 0, 0, 1, uOffsetCornerBottom + innerWidth / 2, vOffsetTopCorner - (cornerRadius - topRadius) / 2]),
                        ),
                        ...texUV_cornerFlatPatches(
                            topology.top.edge.loops[5],
                            flipLoop(topology.top.edge.loops[4]),
                            cornerRadius,
                            topRadius,
                            Matrix3x2.fromArray([1, 0, 0, 1, uOffsetCornerBottom + innerWidth / 2, vOffsetTopCorner + (cornerRadius - topRadius) / 2]),
                            Matrix3x2.fromArray([1, 0, 0, 1, uOffsetCornerRight - innerLength / 2, vOffsetTopCorner + (cornerRadius - topRadius) / 2]),
                        ),
                    },
                    leftTop: transformUV(texUV_default.top.corner.leftTop, defaultTexUVOffset.top.corner.left, [uOffsetCornerLeft, vOffsetTopCorner], 90),
                    leftBottom: transformUV(texUV_default.top.corner.leftBottom, defaultTexUVOffset.top.corner.left, [uOffsetCornerLeft, vOffsetTopCorner], 90),
                    rightTop: transformUV(texUV_default.top.corner.rightTop, defaultTexUVOffset.top.corner.right, [uOffsetCornerRight, vOffsetTopCorner], -90),
                    rightBottom: transformUV(
                        texUV_default.top.corner.rightBottom,
                        defaultTexUVOffset.top.corner.right,
                        [uOffsetCornerRight, vOffsetTopCorner],
                        -90,
                    ),
                    top: transformUV(texUV_default.top.corner.top, defaultTexUVOffset.top.corner.top, [uOffsetCornerTop, vOffsetTopCorner], 180),
                    bottom: transformUV(texUV_default.top.corner.bottom, defaultTexUVOffset.top.corner.bottom, [uOffsetCornerBottom, vOffsetTopCorner], 0),
                },
                edge: topEdge,
            },
        }
    }

    const texUV_AllOverWrap = (): MattressGeometryParts<AttributeRef> => {
        const texUV_default = texUV_Waterfall()
        const texUV_borderWithMirror = texUV_BorderWithMirror()

        const vOffsetBottomEdgeTop = innerLength / 2 + (cornerRadius - topRadius) + (topRadius * Math.PI) / 2 + height + (bottomRadius * Math.PI) / 4
        const vOffsetBottomCornerTop = vOffsetBottomEdgeTop + (bottomRadius * Math.PI) / 4 + (cornerRadius - bottomRadius) / 2
        const vOffsetBottomSquareTop = vOffsetBottomCornerTop + (cornerRadius - bottomRadius) / 2

        const vOffsetBottomEdgeBottom = -vOffsetBottomEdgeTop
        const vOffsetBottomCornerBottom = -vOffsetBottomCornerTop
        const vOffsetBottomSquareBottom = -vOffsetBottomSquareTop

        return {
            ...texUV_borderWithMirror,
            top: {
                square: texUV_default.top.square,
                corner: texUV_default.top.corner,
                edge: texUV_default.top.edge,
            },
            bottom: {
                square: {
                    top: transformUV(texUV_default.bottom.square.top, defaultTexUVOffset.bottom.square.top, [0, vOffsetBottomSquareTop], 180),
                    leftTop: transformUV(texUV_default.bottom.square.leftTop, defaultTexUVOffset.bottom.square.top, [0, vOffsetBottomSquareTop], 180),
                    leftBottom: transformUV(
                        texUV_default.bottom.square.leftBottom,
                        defaultTexUVOffset.bottom.square.bottom,
                        [0, vOffsetBottomSquareBottom],
                        180,
                    ),
                    bottom: transformUV(texUV_default.bottom.square.bottom, defaultTexUVOffset.bottom.square.bottom, [0, vOffsetBottomSquareBottom], 180),
                    rightTop: transformUV(texUV_default.bottom.square.rightTop, defaultTexUVOffset.bottom.square.top, [0, vOffsetBottomSquareTop], 180),
                    rightBottom: transformUV(
                        texUV_default.bottom.square.rightBottom,
                        defaultTexUVOffset.bottom.square.bottom,
                        [0, vOffsetBottomSquareBottom],
                        180,
                    ),
                },
                edge: {
                    top: transformUV(texUV_default.bottom.edge.top, defaultTexUVOffset.bottom.edge.top, [0, vOffsetBottomEdgeTop], 180),
                    leftTop: transformUV(texUV_default.bottom.edge.leftTop, defaultTexUVOffset.bottom.edge.top, [0, vOffsetBottomEdgeTop], 180),
                    rightTop: transformUV(texUV_default.bottom.edge.rightTop, defaultTexUVOffset.bottom.edge.top, [0, vOffsetBottomEdgeTop], 180),
                    bottom: transformUV(texUV_default.bottom.edge.bottom, defaultTexUVOffset.bottom.edge.bottom, [0, vOffsetBottomEdgeBottom], 180),
                    leftBottom: transformUV(texUV_default.bottom.edge.leftBottom, defaultTexUVOffset.bottom.edge.bottom, [0, vOffsetBottomEdgeBottom], 180),
                    rightBottom: transformUV(texUV_default.bottom.edge.rightBottom, defaultTexUVOffset.bottom.edge.bottom, [0, vOffsetBottomEdgeBottom], 180),
                    topLeft: transformUV(texUV_default.bottom.edge.topLeft, defaultTexUVOffset.bottom.edge.top, [0, vOffsetBottomEdgeTop], 180),
                    topRight: transformUV(texUV_default.bottom.edge.topRight, defaultTexUVOffset.bottom.edge.top, [0, vOffsetBottomEdgeTop], 180),
                    bottomLeft: transformUV(texUV_default.bottom.edge.bottomLeft, defaultTexUVOffset.bottom.edge.bottom, [0, vOffsetBottomEdgeBottom], 180),
                    bottomRight: transformUV(texUV_default.bottom.edge.bottomRight, defaultTexUVOffset.bottom.edge.bottom, [0, vOffsetBottomEdgeBottom], 180),
                },
                corner: {
                    top: transformUV(texUV_default.bottom.corner.top, defaultTexUVOffset.bottom.corner.top, [0, vOffsetBottomCornerTop], 180),
                    leftTop: transformUV(texUV_default.bottom.corner.leftTop, defaultTexUVOffset.bottom.corner.top, [0, vOffsetBottomCornerTop], 180),
                    rightTop: transformUV(texUV_default.bottom.corner.rightTop, defaultTexUVOffset.bottom.corner.top, [0, vOffsetBottomCornerTop], 180),
                    bottom: transformUV(texUV_default.bottom.corner.bottom, defaultTexUVOffset.bottom.corner.bottom, [0, vOffsetBottomCornerBottom], 180),
                    leftBottom: transformUV(
                        texUV_default.bottom.corner.leftBottom,
                        defaultTexUVOffset.bottom.corner.bottom,
                        [0, vOffsetBottomCornerBottom],
                        180,
                    ),
                    rightBottom: transformUV(
                        texUV_default.bottom.corner.rightBottom,
                        defaultTexUVOffset.bottom.corner.bottom,
                        [0, vOffsetBottomCornerBottom],
                        180,
                    ),
                    topLeft: transformQuarterDisc(texUV_default.bottom.corner.topLeft, defaultTexUVOffset.bottom.corner.top, [0, vOffsetBottomCornerTop], 180),
                    topRight: transformQuarterDisc(
                        texUV_default.bottom.corner.topRight,
                        defaultTexUVOffset.bottom.corner.top,
                        [0, vOffsetBottomCornerTop],
                        180,
                    ),
                    bottomLeft: transformQuarterDisc(
                        texUV_default.bottom.corner.bottomLeft,
                        defaultTexUVOffset.bottom.corner.bottom,
                        [0, vOffsetBottomCornerBottom],
                        180,
                    ),
                    bottomRight: transformQuarterDisc(
                        texUV_default.bottom.corner.bottomRight,
                        defaultTexUVOffset.bottom.corner.bottom,
                        [0, vOffsetBottomCornerBottom],
                        180,
                    ),
                },
            },
            sides: {
                ...texUV_borderWithMirror.sides,
                top: texUV_default.sides.top,
                bottom: texUV_default.sides.bottom,
                left: {
                    halfCorner1: transformUV(
                        texUV_default.sides.left.halfCorner1,
                        defaultTexUVOffset.sides.left.halfCorner1,
                        [-innerWidth / 2 - 1.5 * halfCornerCircumfrence, defaultTexUVOffset.sides.bottom.side[1]],
                        90,
                    ),
                    side: texUV_default.sides.left.side,
                    halfCorner2: transformUV(
                        texUV_default.sides.left.halfCorner2,
                        defaultTexUVOffset.sides.left.halfCorner2,
                        [-innerWidth / 2 - 1.5 * halfCornerCircumfrence, defaultTexUVOffset.sides.top.side[1]],
                        -90,
                    ),
                },
                right: {
                    halfCorner1: transformUV(
                        texUV_default.sides.right.halfCorner1,
                        defaultTexUVOffset.sides.right.halfCorner1,
                        [innerWidth / 2 + 1.5 * halfCornerCircumfrence, defaultTexUVOffset.sides.top.side[1]],
                        90,
                    ),
                    side: texUV_default.sides.right.side,
                    halfCorner2: transformUV(
                        texUV_default.sides.right.halfCorner2,
                        defaultTexUVOffset.sides.right.halfCorner2,
                        [innerWidth / 2 + 1.5 * halfCornerCircumfrence, defaultTexUVOffset.sides.bottom.side[1]],
                        -90,
                    ),
                },
            },

            // top: texUV_default.top,
            // sides: texUV_default.sides,
            // bottom: {
            //     square: transformUV(
            //         texUV_default.bottom.square,
            //         [0, 0],
            //         [0, defaultTexUVOffset.sides.bottom.side[1] - height / 2 - (bottomRadius * Math.PI) / 2 - (cornerRadius - bottomRadius) - innerLength / 2],
            //         180,
            //     ),
            //     corner: {
            //         ...texUV_default.bottom.corner,
            //         bottom: transformUV(
            //             geometry.bottom.corner.bottom.texUV,
            //             defaultTexUVOffset.bottom.corner.bottom,
            //             [0, defaultTexUVOffset.sides.bottom.side[1] - height / 2 - (bottomRadius * Math.PI) / 2 - (cornerRadius - bottomRadius) / 2],
            //             180,
            //         ),
            //         // right: transformUV(geometry.bottom.corner.right.texUV, defaultTexUVOffset.bottom.corner.right, [
            //         //     defaultTexUVOffset.bottom.corner.right[0],
            //         //     defaultTexUVOffset.sides.bottom.side[1] - height / 2 - (bottomRadius * Math.PI) / 2 - (cornerRadius - bottomRadius) - innerLength / 2,
            //         // ]),

            //         right: transformUV(geometry.bottom.corner.right.texUV, defaultTexUVOffset.bottom.corner.right, [
            //             defaultTexUVOffset.bottom.corner.right[0],
            //             defaultTexUVOffset.sides.bottom.side[1] - height / 2 - (bottomRadius * Math.PI) / 2 - (cornerRadius - bottomRadius) - innerLength / 2,
            //         ]),
            //     },
            //     edge: {
            //         ...texUV_default.bottom.edge,
            //         bottom: transformUV(
            //             geometry.bottom.edge.bottom.texUV,
            //             defaultTexUVOffset.bottom.edge.bottom,
            //             [0, defaultTexUVOffset.sides.bottom.side[1] - height / 2 - (bottomRadius * Math.PI) / 4],
            //             180,
            //         ),
            //         bottomLeft: transformUV(
            //             geometry.bottom.edge.bottomLeft.texUV,
            //             defaultTexUVOffset.bottom.edge.bottomLeft,
            //             [
            //                 defaultTexUVOffset.bottom.edge.bottomLeft[0],
            //                 defaultTexUVOffset.sides.bottom.side[1] - height / 2 - (cornerRadius - bottomRadius + (bottomRadius * Math.PI) / 2) / 2,
            //             ],
            //             180,
            //         ),
            //         bottomRight: transformUV(
            //             geometry.bottom.edge.bottomRight.texUV,
            //             defaultTexUVOffset.bottom.edge.bottomRight,
            //             [
            //                 defaultTexUVOffset.bottom.edge.bottomRight[0],
            //                 defaultTexUVOffset.sides.bottom.side[1] - height / 2 - (cornerRadius - bottomRadius + (bottomRadius * Math.PI) / 2) / 2,
            //             ],
            //             180,
            //         ),
            //         // topLeft: transformUV(geometry.bottom.edge.topLeft.texUV, defaultTexUVOffset.bottom.edge.topLeft, [0, 0]),
            //         topRight: transformUV(geometry.bottom.edge.topRight.texUV, defaultTexUVOffset.bottom.edge.topRight, [0, 0]),
            //         // bottomLeft: transformUV(geometry.bottom.edge.bottomLeft.texUV, defaultTexUVOffset.bottom.edge.bottomLeft, [0, 0]),
            //     },
            // },
        }
    }

    return {
        texUV_Waterfall: texUV_Waterfall(),
        texUV_InvertedWaterfall: texUV_InvertedWaterfall(),
        texUV_Border: texUV_Border(),
        texUV_BorderWithMirror: texUV_BorderWithMirror(),
        texUV_AllOverWrap: texUV_AllOverWrap(),
    }
}

function mattressPartID(geometry: MattressGeometryParts<PartAttributes>, topology: MattressTopology): MattressGeometryParts<AttributeRef> {
    let partID_ = 0
    const partID = (attr: AttributeRef) => attr.constInt(partID_++)

    // TODO
    const partIDForLoopPair = (loopA: AttributeRef, loopB: AttributeRef): AttributeRef => {
        let {a1} = Op.disjointProduct(loopA, {a1: loopA}, loopB, {b1: loopB})
        return partID(a1)
    }

    return {
        top: {
            square: {
                top: partID(geometry.top.square.top.texUV),
                leftTop: partID(geometry.top.square.leftTop.texUV),
                leftBottom: partID(geometry.top.square.leftBottom.texUV),
                bottom: partID(geometry.top.square.bottom.texUV),
                rightTop: partID(geometry.top.square.rightTop.texUV),
                rightBottom: partID(geometry.top.square.rightBottom.texUV),
            },
            corner: {
                bottom: partID(geometry.top.corner.bottom.texUV),
                leftTop: partID(geometry.top.corner.leftTop.texUV),
                leftBottom: partID(geometry.top.corner.leftBottom.texUV),
                rightTop: partID(geometry.top.corner.rightTop.texUV),
                rightBottom: partID(geometry.top.corner.rightBottom.texUV),
                top: partID(geometry.top.corner.top.texUV),
                bottomLeft: {
                    curvedPatch1: partIDForLoopPair(flipLoop(topology.top.edge.loops[2]), topology.top.corner.innerLoop),
                    curvedPatch2: partIDForLoopPair(topology.top.edge.loops[5], topology.top.corner.innerLoop),
                    flatPatch1: partID(geometry.top.corner.bottomLeft.flatPatch1.texUV),
                    flatPatch2: partID(geometry.top.corner.bottomLeft.flatPatch2.texUV),
                },
                bottomRight: {
                    curvedPatch1: partIDForLoopPair(topology.top.edge.loops[5], topology.top.corner.innerLoop),
                    curvedPatch2: partIDForLoopPair(flipLoop(topology.top.edge.loops[4]), topology.top.corner.innerLoop),
                    flatPatch1: partID(geometry.top.corner.bottomRight.flatPatch1.texUV),
                    flatPatch2: partID(geometry.top.corner.bottomRight.flatPatch2.texUV),
                },
                topLeft: {
                    curvedPatch1: partIDForLoopPair(topology.top.edge.loops[1], topology.top.corner.innerLoop),
                    curvedPatch2: partIDForLoopPair(flipLoop(topology.top.edge.loops[2]), topology.top.corner.innerLoop),
                    flatPatch1: partID(geometry.top.corner.topLeft.flatPatch1.texUV),
                    flatPatch2: partID(geometry.top.corner.topLeft.flatPatch2.texUV),
                },
                topRight: {
                    curvedPatch1: partIDForLoopPair(flipLoop(topology.top.edge.loops[4]), topology.top.corner.innerLoop),
                    curvedPatch2: partIDForLoopPair(topology.top.edge.loops[1], topology.top.corner.innerLoop),
                    flatPatch1: partID(geometry.top.corner.topRight.flatPatch1.texUV),
                    flatPatch2: partID(geometry.top.corner.topRight.flatPatch2.texUV),
                },
            },
            edge: {
                bottom: partID(geometry.top.edge.bottom.texUV),
                leftTop: partID(geometry.top.edge.leftTop.texUV),
                leftBottom: partID(geometry.top.edge.leftBottom.texUV),
                rightTop: partID(geometry.top.edge.rightTop.texUV),
                rightBottom: partID(geometry.top.edge.rightBottom.texUV),
                top: partID(geometry.top.edge.top.texUV),
                bottomLeft: Op.merge(
                    partIDForLoopPair(flipLoop(topology.top.edge.loops[2]), topology.top.edge.innerLoop),
                    partIDForLoopPair(topology.top.edge.loops[5], topology.top.edge.innerLoop),
                ),
                bottomRight: Op.merge(
                    partIDForLoopPair(topology.top.edge.loops[5], topology.top.edge.innerLoop),
                    partIDForLoopPair(flipLoop(topology.top.edge.loops[4]), topology.top.edge.innerLoop),
                ),
                topLeft: Op.merge(
                    partIDForLoopPair(topology.top.edge.loops[1], topology.top.edge.innerLoop),
                    partIDForLoopPair(flipLoop(topology.top.edge.loops[2]), topology.top.edge.innerLoop),
                ),
                topRight: Op.merge(
                    partIDForLoopPair(flipLoop(topology.top.edge.loops[4]), topology.top.edge.innerLoop),
                    partIDForLoopPair(topology.top.edge.loops[1], topology.top.edge.innerLoop),
                ),
            },
        },
        sides: {
            bottom: {
                halfCorner1: partID(geometry.sides.bottom.halfCorner1.texUV),
                side: partID(geometry.sides.bottom.side.texUV),
                halfCorner2: partID(geometry.sides.bottom.halfCorner2.texUV),
            },
            left: {
                halfCorner1: partID(geometry.sides.left.halfCorner1.texUV),
                side: partID(geometry.sides.left.side.texUV),
                halfCorner2: partID(geometry.sides.left.halfCorner2.texUV),
            },
            right: {
                halfCorner1: partID(geometry.sides.right.halfCorner1.texUV),
                side: partID(geometry.sides.right.side.texUV),
                halfCorner2: partID(geometry.sides.right.halfCorner2.texUV),
            },
            top: {
                halfCorner1: partID(geometry.sides.top.halfCorner1.texUV),
                side: partID(geometry.sides.top.side.texUV),
                halfCorner2: partID(geometry.sides.top.halfCorner2.texUV),
            },
        },
        bottom: {
            square: {
                top: partID(geometry.bottom.square.top.texUV),
                leftTop: partID(geometry.bottom.square.leftTop.texUV),
                leftBottom: partID(geometry.bottom.square.leftBottom.texUV),
                bottom: partID(geometry.bottom.square.bottom.texUV),
                rightTop: partID(geometry.bottom.square.rightTop.texUV),
                rightBottom: partID(geometry.bottom.square.rightBottom.texUV),
            },
            corner: {
                bottom: partID(geometry.bottom.corner.bottom.texUV),
                leftTop: partID(geometry.bottom.corner.leftTop.texUV),
                leftBottom: partID(geometry.bottom.corner.leftBottom.texUV),
                rightTop: partID(geometry.bottom.corner.rightTop.texUV),
                rightBottom: partID(geometry.bottom.corner.rightBottom.texUV),
                top: partID(geometry.bottom.corner.top.texUV),
                bottomLeft: {
                    curvedPatch1: partIDForLoopPair(flipLoop(topology.bottom.edge.loops[2]), topology.bottom.corner.innerLoop),
                    curvedPatch2: partIDForLoopPair(topology.bottom.edge.loops[5], topology.bottom.corner.innerLoop),
                    flatPatch1: partID(geometry.bottom.corner.bottomLeft.flatPatch1.texUV),
                    flatPatch2: partID(geometry.bottom.corner.bottomLeft.flatPatch2.texUV),
                },
                bottomRight: {
                    curvedPatch1: partIDForLoopPair(topology.bottom.edge.loops[5], topology.bottom.corner.innerLoop),
                    curvedPatch2: partIDForLoopPair(flipLoop(topology.bottom.edge.loops[4]), topology.bottom.corner.innerLoop),
                    flatPatch1: partID(geometry.bottom.corner.bottomRight.flatPatch1.texUV),
                    flatPatch2: partID(geometry.bottom.corner.bottomRight.flatPatch2.texUV),
                },
                topLeft: {
                    curvedPatch1: partIDForLoopPair(topology.bottom.edge.loops[1], topology.bottom.corner.innerLoop),
                    curvedPatch2: partIDForLoopPair(flipLoop(topology.bottom.edge.loops[2]), topology.bottom.corner.innerLoop),
                    flatPatch1: partID(geometry.bottom.corner.topLeft.flatPatch1.texUV),
                    flatPatch2: partID(geometry.bottom.corner.topLeft.flatPatch2.texUV),
                },
                topRight: {
                    curvedPatch1: partIDForLoopPair(flipLoop(topology.bottom.edge.loops[4]), topology.bottom.corner.innerLoop),
                    curvedPatch2: partIDForLoopPair(topology.bottom.edge.loops[1], topology.bottom.corner.innerLoop),
                    flatPatch1: partID(geometry.bottom.corner.topRight.flatPatch1.texUV),
                    flatPatch2: partID(geometry.bottom.corner.topRight.flatPatch2.texUV),
                },
            },
            edge: {
                bottom: partID(geometry.bottom.edge.bottom.texUV),
                leftTop: partID(geometry.bottom.edge.leftTop.texUV),
                leftBottom: partID(geometry.bottom.edge.leftBottom.texUV),
                rightTop: partID(geometry.bottom.edge.rightTop.texUV),
                rightBottom: partID(geometry.bottom.edge.rightBottom.texUV),
                top: partID(geometry.bottom.edge.top.texUV),
                bottomLeft: Op.merge(
                    partIDForLoopPair(flipLoop(topology.bottom.edge.loops[2]), topology.bottom.edge.innerLoop),
                    partIDForLoopPair(topology.bottom.edge.loops[5], topology.bottom.edge.innerLoop),
                ),
                bottomRight: Op.merge(
                    partIDForLoopPair(topology.bottom.edge.loops[5], topology.bottom.edge.innerLoop),
                    partIDForLoopPair(flipLoop(topology.bottom.edge.loops[4]), topology.bottom.edge.innerLoop),
                ),
                topLeft: Op.merge(
                    partIDForLoopPair(topology.bottom.edge.loops[1], topology.bottom.edge.innerLoop),
                    partIDForLoopPair(flipLoop(topology.bottom.edge.loops[2]), topology.bottom.edge.innerLoop),
                ),
                topRight: Op.merge(
                    partIDForLoopPair(flipLoop(topology.bottom.edge.loops[4]), topology.bottom.edge.innerLoop),
                    partIDForLoopPair(topology.bottom.edge.loops[1], topology.bottom.edge.innerLoop),
                ),
            },
        },
    }
}

// the attributes carried by every one of the geometry parts
type MattressPartAttributes = {
    pos: AttributeRef
    manUV: AttributeRef
    partID: AttributeRef
} & Record<MattressTexUVLayout, AttributeRef>

export function mattressGeometryParts(ctx: GeomBuilderContext, params: InternalMattressParams): MattressGeometryParts<MattressPartAttributes> {
    const {
        cornerRadiusSegments,
        topRadiusSegments,
        bottomRadiusSegments,
        widthSegments,
        heightSegments,
        lengthSegments,
        width,
        length,
        height,
        cornerRadius,
        topRadius,
        bottomRadius,
        maxCornerRadius,
        maxTopRadius,
        maxBottomRadius,
    } = params

    const bottomLoopSegments = Math.max(widthSegments, lengthSegments)
    const cornerInnerLoopSegments = 10

    const {geometry, topology} = (() => {
        // start with the bottom (punctured) square
        const [bottomSquareGeom, iface1] = bottomSquare(ctx, {
            width: width - cornerRadius * 2,
            length: length - cornerRadius * 2,
            punctureSizeFactor: 0.01,
            widthSegmentCount: widthSegments,
            lengthSegmentCount: lengthSegments,
            innerLoopSegmentCount: bottomLoopSegments,
        })

        // ...then add the bottom inner corner radius
        const [bottomCornerGeom, iface2] = bottomCornerRadius(
            ctx,
            {
                innerCornerRadius: cornerRadius - bottomRadius,
                radiusAngularSegmentCount: cornerRadiusSegments,
                radiusParallelSegmentCount: cornerInnerLoopSegments,
            },
            iface1,
        )

        // ...then curve upwards with the bottom radius
        const [bottomEdgeGeom, iface3] = bottomEdgeRadius(
            ctx,
            {
                radiusAngularSegmentCount: bottomRadiusSegments,
                edgeRadius: bottomRadius,
            },
            iface2,
        )

        // ...then add the sides
        const [sidesGeom, iface4] = sides(
            ctx,
            {
                topRadius,
                height,
                loopSegmentCount: heightSegments,
            },
            iface3,
        )

        // ..then curve inwards with the top radius
        const [topEdgeGeom, iface5] = topEdgeRadius(
            ctx,
            {
                radiusAngularSegmentCount: topRadiusSegments,
                edgeRadius: topRadius,
            },
            iface4,
        )

        // ...then remove the top inner corner radius
        const [topCornerGeom, iface6] = topCornerRadius(
            ctx,
            {
                radiusParallelSegmentCount: cornerInnerLoopSegments,
            },
            iface5,
        )

        /// ...finally close the top with a square
        const topSquareGeom = topSquare(ctx, iface6)

        return {
            geometry: {
                bottom: {
                    square: bottomSquareGeom,
                    corner: bottomCornerGeom,
                    edge: bottomEdgeGeom,
                },
                sides: sidesGeom,
                top: {
                    edge: topEdgeGeom,
                    corner: topCornerGeom,
                    square: topSquareGeom,
                },
            },
            topology: {
                bottom: {
                    square: iface1,
                    corner: iface2,
                    edge: iface3,
                },
                sides: iface4,
                top: {
                    edge: iface5,
                    corner: iface6,
                    // square: {},
                },
            },
        }
    })()

    const texUVs = mattressTexUVs(geometry, topology, {
        cornerRadiusSegments,
        topRadiusSegments,
        bottomRadiusSegments,
        widthSegments,
        heightSegments,
        lengthSegments,
        topRadius,
        cornerRadius,
        bottomRadius,
        maxTopRadius,
        maxCornerRadius,
        maxBottomRadius,
        width,
        length,
        height,
        resolutionMultiplier: params.resolutionMultiplier ?? 1,
    })

    const partID = mattressPartID(geometry, topology)

    const zipped = zipParts({geometry, partID, ...texUVs})

    return mapParts(zipped, ({geometry: geom, partID: id, ...texUVLayouts}) => ({
        pos: geom.pos,
        manUV: geom.manUV,
        partID: id,
        ...texUVLayouts,
    }))
}

type MattressAttribute = keyof MattressPartAttributes

export function mattressAttrsInternal<T extends readonly MattressAttribute[]>(
    ctx: GeomBuilderContext,
    params: InternalMattressParams,
    selection: T,
): {[K in T[number]]: AttributeRef} {
    const parts = flattenParts(mattressGeometryParts(ctx, params))
    const mergedAttributes = Op.merge(...parts.map((part) => Object.fromEntries(selection.map((attribute) => [attribute, part[attribute]] as const))))

    if (mergedAttributes["pos"]) mergedAttributes["pos"] = mergedAttributes["pos"].weld(1e-4)
    if (mergedAttributes["manUV"]) mergedAttributes["manUV"] = mergedAttributes["manUV"].weld(1e-5)
    if (mergedAttributes["texUV_Waterfall"]) mergedAttributes["texUV_Waterfall"] = mergedAttributes["texUV_Waterfall"].weld(1e-4)
    if (mergedAttributes["texUV_InvertedWaterfall"]) mergedAttributes["texUV_InvertedWaterfall"] = mergedAttributes["texUV_InvertedWaterfall"].weld(1e-4)
    if (mergedAttributes["texUV_Border"]) mergedAttributes["texUV_Border"] = mergedAttributes["texUV_Border"].weld(1e-4)
    if (mergedAttributes["texUV_BorderWithMirror"]) mergedAttributes["texUV_BorderWithMirror"] = mergedAttributes["texUV_BorderWithMirror"].weld(1e-4)
    if (mergedAttributes["texUV_AllOverWrap"]) mergedAttributes["texUV_AllOverWrap"] = mergedAttributes["texUV_AllOverWrap"].weld(1e-4)

    return mergedAttributes as {[K in T[number]]: AttributeRef}
}

export const mattressParams = z.object({
    cornerRadiusSegments: z.number(),
    topRadiusSegments: z.number(),
    bottomRadiusSegments: z.number(),
    widthSegments: z.number(),
    heightSegments: z.number(),
    lengthSegments: z.number(),
    topRadius: z.number(),
    cornerRadius: z.number(),
    bottomRadius: z.number(),
    width: z.number(),
    length: z.number(),
    height: z.number(),
    resolutionMultiplier: z.number(),
})
export type MattressParams = Partial<z.infer<typeof mattressParams>>

export function getSafeInternalMattressParams(params: MattressParams): Required<InternalMattressParams> {
    const eps = 1e-3
    const epsMultiplier = 5

    // NOTE bd width and length params are swapped compared to mattress internal width and length params
    const widthParam = params.length ?? 140
    const lengthParam = params.width ?? 200
    const heightParam = params.height ?? 25
    const bottomRadiusParam = params.bottomRadius ?? 5
    const topRadiusParam = params.topRadius ?? 5
    const cornerRadiusParam = params.cornerRadius ?? 10

    const maxCornerRadius = Math.min(widthParam, lengthParam) / 2 - eps
    const cornerRadius = Math.max(eps, Math.min(cornerRadiusParam, maxCornerRadius))
    const maxTopRadius = Math.min(heightParam / 2, cornerRadius) - eps * epsMultiplier
    const topRadius = Math.max(eps, Math.min(topRadiusParam, maxTopRadius))
    const maxBottomRadius = Math.min(heightParam / 2, cornerRadius) - eps * epsMultiplier
    const bottomRadius = Math.max(eps, Math.min(bottomRadiusParam, maxBottomRadius))

    const height = heightParam - topRadius - bottomRadius

    const lengthSegments = params.widthSegments ?? 10
    const widthSegments = params.lengthSegments ?? 10
    const heightSegments = params.heightSegments ?? 10
    const cornerRadiusSegments = params.cornerRadiusSegments ?? 10
    const topRadiusSegments = params.topRadiusSegments ?? 10
    const bottomRadiusSegments = params.bottomRadiusSegments ?? 10

    return {
        cornerRadiusSegments,
        topRadiusSegments,
        bottomRadiusSegments,
        widthSegments,
        heightSegments,
        lengthSegments,
        width: widthParam,
        length: lengthParam,
        height,
        cornerRadius,
        topRadius,
        bottomRadius,
        maxCornerRadius,
        maxTopRadius,
        maxBottomRadius,
        resolutionMultiplier: params.resolutionMultiplier ?? 1,
    }
}

export function mattressAttrs<T extends readonly MattressAttribute[]>(
    ctx: GeomBuilderContext,
    params: MattressParams,
    selection: T,
): {[K in T[number]]: AttributeRef} {
    const attrs = mattressAttrsInternal(ctx, getSafeInternalMattressParams(params), selection)
    return attrs
}

export function mattress(params: MattressParams): StandardMeshGeometryAttributes {
    const ctx = new GeomBuilderContext()
    const attrs = mattressAttrsInternal(ctx, getSafeInternalMattressParams(params), [
        "pos",
        "manUV",
        "partID",
        "texUV_Waterfall",
        "texUV_InvertedWaterfall",
        "texUV_Border",
        "texUV_BorderWithMirror",
        "texUV_AllOverWrap",
    ])

    return {
        position: attrs.pos,
        normal: attrs.pos.normals(1),
        materialID: attrs.pos.constInt(0),
        uvs: [attrs.texUV_Waterfall, attrs.manUV, attrs.texUV_InvertedWaterfall, attrs.texUV_Border],
    }
}
